import { useState, useEffect, useMemo, useRef } from 'react'
import "form-pattern/dist/main.css";
import Table from '../../../components/common/Table/Table';
import Modal from '../../../components/common/Modal/Modal';
import TooltipBanner from '../../../components/common/TooltipBanner'
import ValidationBanner from '../../../components/common/ValidationBanner'
import ValidationErrorDisplay from '../../../components/common/FormFields/ValidationErrorDisplay'
import Dropdown, { DropdownOption } from '../../../components/common/Dropdown';
import { validateEntries, validateDateGaps } from '../../../components/common/Validation/validationEngine'
import ValidationMessages from '../../../components/common/Validation/validationMessageConfig'
import { formatTime, hoursOptions, minutesOptions, ampmOptions, formatManualRequestReceivedDate } from '../../../utils/date'
import { isoToUTCMinutes, timeFieldsToMinutes, doTimeRangesOverlap } from '../../../utils/partialDayUtils'
import { scrollToTopAndFocusAlert } from '../../../utils/scrollUtils'
import { formatHours } from '../../../utils/formatters'
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import TextInputField from '../../../components/common/FormFields/TextInputField';
import { useTimeOffBalance } from '../../../hooks/useTimeOffBalance'
import { Balance, BalanceHoursTotals, calculateResultingBalances, HoursValues } from '../../../utils/balanceUtils'
import { useCreateTimeOffRequest } from '../../../hooks/useCreateTimeOffRequest'
import { determineRequestType } from '../../../utils/requestTypeUtils'
import { extractApiError } from '../../../utils/apiError'
import Loader from '../../../components/common/Loader'
import ManualRequestedDate from '../../../components/common/ManualRequestedDate';
import LabeledValue from '../../../components/common/LabeledValue';
import { trackFormSubmit, trackFormSave, trackFormError } from '../../../services/analytics'
import { RootState } from '../../../store/store'

interface AddTimeToRequestProps {
  selectedDates: string[];
  // Dates already committed via Next/Update. Used to separate the right-panel
  // display into reviewed (committed) dates vs newly added in-progress dates.
  committedDates?: string[];
  onValidationError?: (messages: string[] | null) => void
  onCancelRequest?: () => void
  // when parent validation CTA is clicked, increment this counter to focus/highlight Other subtype dropdown
  focusOtherSubtypeSignal?: number
  // when parent wants the component to exit the review view, increment this counter
  exitAddTimeReviewSignal?: number
  // notify parent when review state changes
  onAddTimeReviewChange?: (isReview: boolean) => void
  // edit mode: when true, component shows "Edit your request" and allows field modification
  editMode?: boolean;
  // Start directly in review step (skip form step). Used for EditSubmittedRequest
  startInReview?: boolean;
  // Is this a resubmission flow? Used to call submitResubmitted instead of submitFinal
  isResubmitFlow?: boolean;
  // Whether the original request was a birthday request (resubmit flow only)
  // If true, birthday checkbox will be auto-checked on load (user can uncheck/change)
  isBirthdayRequest?: boolean;
  // Whether the request being edited is a Vacation Bid. The 8-day gap rule
  // (validateDateGaps) is a Vacation Bid-only business rule and must not fire
  // for normal resubmit requests.
  isVacationBidRequest?: boolean;
  editDate?: string | null;
  // Callback when edit date is clicked in review table
  onEditDateClick?: (date: string) => void;
  // Callback when remove date button is clicked
  onRemoveDateClick?: (date: string) => void;
  // Callback when exiting edit mode (after successful Update)
  onExitEditMode?: () => void;
  // Callback when Back is clicked from edit form
  onBackFromEditMode?: () => void;
  // Employee context for admin mode
  employeeId?: string;
  employeeName?: string;
  // DraftView integration: hide the review table built into this component
  hideReviewTable?: boolean;
  // Callback when the review rows are computed
  onReviewReady?: (
    rows: Array<{
      id: string
      requestType: string
      date: string
      day: string
      times: string
      requestedHours: string
      hoursInOtherRequests: string
      reviewHoursValues?: HoursValues
      reviewDuration?: 'Full Day' | 'Partial Day'
      reviewTime?: { from: TimeFields; to: TimeFields }
      reviewOtherSubType?: string
    }>
  ) => void
  // Pre-populated per-date hours storage for EditSubmittedRequest
  initialPerDateHoursStorage?: Record<string, any>;
  // Pre-populated per-date duration storage for EditSubmittedRequest
  initialPerDateDurationStorage?: Record<string, string>;
  // Pre-populated per-date time storage for partial days in EditSubmittedRequest
  initialPerDateTimeStorage?: Record<string, { from: { hour: string; minute: string; ampm: string }; to: { hour: string; minute: string; ampm: string } }>;
  // Pre-populated per-date "other" subtype storage for EditSubmittedRequest
  initialPerDateOtherSubTypeStorage?: Record<string, string>;
  // Original request ID for resubmission flow (passed as toReqId in payload)
  originalRequestId?: string;
  // Pre-populated comments for EditSubmittedRequest
  initialComments?: string;
  // Existing request id (e.g. a DRAFT being edited). When provided, saving or
  // submitting updates that request in place instead of creating a new one.
  toReqId?: string;
  // Previously entered hours keyed by date (MM/DD/YYYY). Used to pre-fill the
  // form when editing an existing request so values are retained, not shown as 0.
  initialHoursByDate?: Record<string, HoursValues>;
  // Previously selected "Other" subtype code (e.g. 'OTH_JD') keyed by date
  // (MM/DD/YYYY). Used to pre-fill the Other-time-off dropdown when editing an
  // existing request so the selection persists instead of resetting to empty.
  initialOtherSubTypeByDate?: Record<string, string>;
  // Previously saved Full/Partial duration keyed by date (MM/DD/YYYY). Used to
  // restore the duration radio when reopening a single saved date for edit (e.g.
  // a Partial Day date in a DRAFT). Without it the form always defaulted to Full
  // Day, silently converting saved Partial Day dates back to Full Day.
  otherRequestHoursByDate?: OtherRequestHoursByDate
  initialDurationByDate?: Record<string, 'Full Day' | 'Partial Day'>;
  // Previously saved partial-day time window keyed by date (MM/DD/YYYY). Used to
  // restore the start/end time pickers when reopening a saved Partial Day date.
  // Only populated for dates that have a parseable partial-day window.
  initialTimeByDate?: Record<string, { from: { hour?: string; minute?: string; ampm?: string }; to: { hour?: string; minute?: string; ampm?: string } }>;
  // When editing a DRAFT via DraftView (which edits one calendar date at a
  // time), these are the OTHER already-saved dates of the same draft that must
  // be preserved in the submission. Without them, saving sends only the single
  // edited date with `toReqId`, causing the backend to replace the draft and
  // drop every other date. All dates are YYYY-MM-DD. This prop is a no-op for
  // every other caller (none of them pass it).
  preservedDatesPayload?: {
    selectedDates: string[];
    perDateHoursValues: Record<string, HoursValues>;
    perDateDurations?: Record<string, 'Full Day' | 'Partial Day'>;
    perDateTimes?: Record<string, { from: { hour?: string; minute?: string; ampm?: string }; to: { hour?: string; minute?: string; ampm?: string } }>;
    perDateOtherSubTypeValues?: Record<string, string>;
  };
  /**
   * Map of MM/DD/YYYY dates that have OTHER users' partial-day requests,
   * keyed to an array of all their time ranges on that date. When the user
   * opens a form for one of these dates the Full Day radio is disabled and
   * Partial Day is pre-selected to prevent a conflicting full-day request.
   * Using an array ensures the overlap check validates against ALL existing
   * partial requests on the date, not just the first one.
   */
  otherPartialDayDates?: Record<string, Array<{ startTime?: string | null; endTime?: string | null }>>;
  /**
   * Remove multiple dates from the request in a single parent state update.
   * Used by the Req 3 deselect path so that removing several partial-day dates
   * at once does not suffer from the stale-closure problem of calling
   * onRemoveDateClick in a loop.
   */
  onRemoveDatesClick?: (dates: string[]) => void;
  /**
   * Removes the single date currently open in the form from an existing DRAFT
   * (DraftView per-date removal). When provided, a "Remove Date From Request"
   * button renders directly beneath the selected-date display so the user can
   * drop one day from a multi-date draft without deleting the whole request.
   * No-op for every other caller (none pass it).
   */
  onRemoveDate?: () => void;
  /** True while a per-date removal save is in flight — disables the button. */
  isRemovingDate?: boolean;
  /**
   * When true the employee is Salaried/Exempt (EMP_TYPE = 'S').
   * The Partial Day option and its time pickers are hidden — exempt employees
   * may only request Full Day time off (mirrors legacy TORT behaviour).
   */
  isExempt?: boolean;
  /**
   * When true, the component renders a "select a new date" prompt instead of
   * the time-entry form. Set by the parent after the user clicks
   * "Add date to request" so the per-date hours storage is preserved while
   * the user picks the next date from the calendar.
   */
  isAddingMoreDates?: boolean;
  onAddMoreDates?: () => void;
}

type TimeFields = {
  hour?: DropdownOption;
  minute?: DropdownOption;
  ampm?: DropdownOption;
};

const DEFAULT_PARTIAL_TIME_WINDOW: { from: TimeFields; to: TimeFields } = {
  from: {
    hour: { id: 'h-1', label: '1', value: '1' },
    minute: { id: 'm-00', label: '00', value: '00' },
    ampm: { id: 'am', label: 'AM', value: 'AM' },
  },
  to: {
    hour: { id: 'h-1', label: '1', value: '1' },
    minute: { id: 'm-00', label: '00', value: '00' },
    ampm: { id: 'am', label: 'AM', value: 'AM' },
  },
}

interface OtherRequestBreakdownEntry {
  hours: number
  typeAbbreviation: string
}

type OtherRequestHoursByDate = Record<string, number | OtherRequestBreakdownEntry[]>

interface ReviewTableProps {
  selectedDates: string[];
  hoursValues: HoursValues;
  leaveTypeSelected: Record<string, string>;
  selectedDuration: string;
  selectedTime?: { from: TimeFields; to: TimeFields };
  isBirthdayChecked: boolean;
  onEditDate?: (date: string) => void;
  onRemoveDate?: (date: string) => void;
  calculateHoursForDate?: (date: string) => number;
  perDateDurationStorage?: Record<string, string>;
  perDateTimeStorage?: Record<string, { from: TimeFields; to: TimeFields }>;
  perDateHoursStorage?: Record<string, HoursValues>;
  /** Per-date Other Types of Time Off subtype selections. Ensures the review table shows the correct request type per date after individual date edits. */
  perDateOtherSubTypeStorage?: Record<string, string>;
  /** Original birthday date (in YYYY-MM-DD format) - used to preserve birthday request type */
  originalBirthdayDate?: string | null;
  /** Original birthday request type - used to display "Birthday" instead of recalculated type */
  originalBirthdayRequestType?: string | null;
  otherRequestHoursByDate?: OtherRequestHoursByDate
}

/**
 * Helper function to format dates for display (MM/DD/YYYY)
 */
const formatDateForDisplay = (dateStr: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [yyyy, mm, dd] = dateStr.split('-');
    return `${mm}/${dd}/${yyyy}`;
  }
  const date = new Date(dateStr);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

/**
 * Converts an API-format `YYYY-MM-DD` date string to `MM/DD/YYYY` for display.
 * Splits the string directly rather than going through `Date`, so it can't
 * shift a day when the local timezone is behind UTC (unlike `formatDateForDisplay`,
 * which is only safe for locale-formatted date strings, not bare ISO dates).
 */
const formatApiDateForDisplay = (isoDateStr: string): string => {
  const [yyyy, mm, dd] = isoDateStr.split('-');
  return yyyy && mm && dd ? `${mm}/${dd}/${yyyy}` : isoDateStr;
};

/**
 * Normalizes a date string to canonical `YYYY-MM-DD` form so keys in different
 * formats compare reliably. The active edit date can arrive as `MM/DD/YYYY`
 * (from the review-table edit button) while selected-date keys may be
 * `YYYY-MM-DD`; normalizing both avoids timezone-sensitive `Date` parsing when
 * matching the date currently being edited.
 *
 * @param dateStr A date string in `YYYY-MM-DD` or `MM/DD/YYYY` format.
 * @returns The canonical `YYYY-MM-DD` string, or the original input if unrecognized.
 */
const normalizeDateKey = (dateStr: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const mdy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateStr);
  return mdy ? `${mdy[3]}-${mdy[1]}-${mdy[2]}` : dateStr;
};

/**
 * Returns `true` when two date strings refer to the same calendar day,
 * tolerating `YYYY-MM-DD` vs `MM/DD/YYYY` format differences.
 *
 * @param a First date string (or null/undefined).
 * @param b Second date string (or null/undefined).
 * @returns `true` only when both are present and resolve to the same day.
 */
const datesMatch = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => !!a && !!b && normalizeDateKey(a) === normalizeDateKey(b);

/**
 * Looks up `dateStr` in a date-keyed map, tolerating `MM/DD/YYYY` vs
 * `YYYY-MM-DD` differences (see {@link datesMatch}). `initialHoursByDate` and
 * its siblings (`initialDurationByDate`, `initialTimeByDate`,
 * `initialOtherSubTypeByDate`) are always keyed `MM/DD/YYYY`, but callers may
 * look them up with a date in either format — a direct `map[dateStr]` access
 * would silently miss.
 *
 * @param map The date-keyed map to search (may be undefined).
 * @param dateStr The date to look up, in either supported format.
 * @returns The matching value, or `undefined` if no key matches.
 */
function findByMatchingDate<T>(
  map: Record<string, T> | undefined,
  dateStr: string
): T | undefined {
  if (!map) return undefined
  if (Object.hasOwn(map, dateStr)) return map[dateStr]
  const key = Object.keys(map).find((k) => datesMatch(k, dateStr))
  return key ? map[key] : undefined
}

/**
 * Normalizes legacy numeric and new breakdown forms into a consistent array shape
 * for the review table's "Hours in Other Requests" column rendering.
 */
const normalizeOtherRequestBreakdown = (
  value: number | OtherRequestBreakdownEntry[] | undefined
): OtherRequestBreakdownEntry[] => {
  if (Array.isArray(value)) {
    return value.filter((entry) => (Number(entry?.hours) || 0) > 0)
  }
  if (typeof value === 'number' && value > 0) {
    return [{ hours: value, typeAbbreviation: 'Oth' }]
  }
  return []
}

const formatOtherRequestBreakdown = (entries: OtherRequestBreakdownEntry[]): string => {
  if (entries.length === 0) return '0.00'
  return entries
    .map((entry) => `${entry.hours.toFixed(2)} ${entry.typeAbbreviation}`)
    .join(' / ')
}

const getOtherRequestBreakdownForDate = (
  date: string,
  otherRequestHoursByDate?: OtherRequestHoursByDate
): OtherRequestBreakdownEntry[] => {
  const mmddyyyyDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? formatDateForDisplay(date)
    : date
  const rawValue =
    otherRequestHoursByDate?.[mmddyyyyDate] ??
    otherRequestHoursByDate?.[date]
  return normalizeOtherRequestBreakdown(rawValue)
}

const ReviewTable = ({ 
  selectedDates, 
  hoursValues, 
  leaveTypeSelected, 
  selectedDuration, 
  selectedTime, 
  isBirthdayChecked, 
  onEditDate, 
  onRemoveDate,
  calculateHoursForDate,
  perDateDurationStorage,
  perDateTimeStorage,
  perDateHoursStorage,
  perDateOtherSubTypeStorage,
  originalBirthdayDate,
  originalBirthdayRequestType,
  otherRequestHoursByDate,
}: ReviewTableProps) => {
  const getRequestTypeForDate = (date: string): string => {
    // Check if this is the original birthday date and we have the stored request type
    if (date === originalBirthdayDate && originalBirthdayRequestType) {
      return originalBirthdayRequestType
    }
    
    const dateHours = perDateHoursStorage?.[date] || hoursValues
    // Use per-date Other subtype when available so each row reflects its own selection
    const effectiveLeaveTypeSelected = perDateOtherSubTypeStorage?.[date]
      ? { ...leaveTypeSelected, other: perDateOtherSubTypeStorage[date] }
      : leaveTypeSelected
    const requestType = determineRequestType(dateHours, effectiveLeaveTypeSelected, isBirthdayChecked)
    return requestType.displayType
  }

  // Format the time display (FullDay or actual times)
  // Uses global selectedTime by default

  /**
   * Get duration for a specific date
   * Returns per-date duration if available, otherwise returns global duration
   */
  const getDurationForDate = (date: string): string => {
    return perDateDurationStorage?.[date] || selectedDuration
  }

  /**
   * Get time display for a specific date
   * Returns per-date time if available and duration is partial, otherwise returns global time
   */
  const getTimeDisplayForDate = (date: string): string => {
    const duration = getDurationForDate(date)
    if (duration === 'Partial Day' && perDateTimeStorage?.[date]) {
      const dateTime = perDateTimeStorage[date]
      // Lowercase, no space before am/pm to match Figma: "9:00am - 10:00am"
      return formatTime(dateTime.from).toLowerCase().replace(' ', '') + ' - ' + formatTime(dateTime.to).toLowerCase().replace(' ', '')
    }
    // Lowercase, no space before am/pm to match Figma: "9:00am - 10:00am"
    return duration === 'Partial Day' && selectedTime
      ? formatTime(selectedTime.from).toLowerCase().replace(' ', '') + ' - ' + formatTime(selectedTime.to).toLowerCase().replace(' ', '')
      : 'Full Day'
  }

  // Calculate total hours for the request (use per-date if available)
  const calculateTotalHours = (date: string): number => {
    if (calculateHoursForDate) {
      return calculateHoursForDate(date);
    }
    // Fallback: calculate from global hoursValues
    const vacationHours = Number.parseFloat(hoursValues.vacation || '0') || 0;
    const floatHours = Number.parseFloat(hoursValues.float || '0') || 0;
    const flexibleHours = Number.parseFloat(hoursValues.flexible || '0') || 0;
    const otherHours = Number.parseFloat(hoursValues.other || '0') || 0;
    return vacationHours + floatHours + flexibleHours + otherHours;
  };

  // Sort dates and format them
  const sortedFormattedDates = [...selectedDates]
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    .map(date => ({
      original: date,
      formatted: formatDateForDisplay(date),
      // Use full weekday name (e.g. "Monday") to match Figma — not the abbreviated "Mon"
      dayName: new Date(date).toLocaleDateString('en-US', { weekday: 'long' }),
    }));

  const getTableRows = () => {
    const dataRows = sortedFormattedDates.map((dateObj) => {
      const hoursForThisDate = calculateTotalHours(dateObj.original)
      const durationForDate = getDurationForDate(dateObj.original)
      const timeDisplayForDate = getTimeDisplayForDate(dateObj.original)
      const requestTypeForDate = getRequestTypeForDate(dateObj.original)
      const otherRequestBreakdown = getOtherRequestBreakdownForDate(
        dateObj.original,
        otherRequestHoursByDate
      )
      const otherRequestHoursTotal = otherRequestBreakdown.reduce(
        (sum, entry) => sum + (Number(entry.hours) || 0),
        0
      )
      const isBirthdayDate =
        (dateObj.original === originalBirthdayDate && !!originalBirthdayRequestType) ||
        (isBirthdayChecked && sortedFormattedDates.length === 1)
      
      return {
        id: `row-${dateObj.formatted}`,
        requestType: requestTypeForDate,
        date: dateObj.formatted,
        day: dateObj.dayName,
        timesRequested: durationForDate === 'Full Day' ? 'Full Day' : timeDisplayForDate,
        hours: isBirthdayDate ? 'NA' : hoursForThisDate.toFixed(2),
        hoursOtherRequests: formatOtherRequestBreakdown(otherRequestBreakdown),
        _otherRequestHoursTotal: otherRequestHoursTotal,
        edit: (
          <button
            className='flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80'
            type='button'
            aria-label='Edit'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEditDate?.(dateObj.formatted);
            }}
          >
            <img src='/assets/images/pencil-outline.svg' alt='Edit' className='w-4 h-4' />
          </button>
        ),
        remove: (
          <button
            className='flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80'
            type='button'
            aria-label='Remove'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemoveDate?.(dateObj.original);
            }}
          >
            <img src='/assets/images/minus-circle-outline.svg' alt='Remove' className='w-4 h-4' />
          </button>
        ),
      };
    });

    const totalHoursNum = dataRows.reduce((sum, row) => sum + (Number.parseFloat(row.hours) || 0), 0);
    const totalOtherRequestsNum = dataRows
      .reduce((sum, row) => sum + row._otherRequestHoursTotal, 0)

    // Add totals row at the end
    const totalsRow = {
      id: 'totals-row',
      requestType: 'Total Hours',
      date: '',
      day: '',
      timesRequested: '',
      hours: formatHours(totalHoursNum),
      hoursOtherRequests: formatHours(totalOtherRequestsNum),
      edit: '',
      remove: '',
      _totalRow: true,
      _rowStyle: {
        backgroundColor: '#D3D6DC',
        border: '1px solid #878F9B'
      },
      _otherRequestHoursTotal: totalOtherRequestsNum,
    };

    return [...dataRows, totalsRow];
  };

  const columns = [
    { key: 'requestType', label: 'Request Type' },
    { key: 'date', label: 'Date' },
    { key: 'day', label: 'Day' },
    { key: 'timesRequested', label: 'Time Requested' },
    { key: 'hours', label: 'Hours' },
    { key: 'hoursOtherRequests', label: 'Hours in Other Requests' },
    { key: 'edit', label: 'Edit' },
    { key: 'remove', label: 'Remove' },
  ];

  const rows = getTableRows();

  return <Table columns={columns} rows={rows} showPagination={false} />;
};

/**
 * Checks min/max duration constraints for all leave-type fields in `hours`.
 * Returns field-level 'Revise' markers and banner messages for any violation.
 * Used to surface errors immediately when the user adds a new date to the
 * picker while the form still shows invalid hours — without waiting for the
 * "Next" button click.
 */
function findDurationViolations(hours: HoursValues): {
  fieldErrors: Record<string, string>
  messages: string[]
} {
  const MIN_HOURS_LIMIT = 0.25
  const FLEXIBLE_MIN_HOURS_LIMIT = 2
  const MAX_HOURS_LIMIT = 16
  const checks: Array<[keyof HoursValues, string]> = [
    ['vacation', ValidationMessages.DURATION],
    ['float', ValidationMessages.FLOAT_DURATION],
    ['flexible', ValidationMessages.FLEXIBLE_MIN_TWO_HOURS],
    ['other', ValidationMessages.OTHER_DURATION],
  ]

  const fieldErrors: Record<string, string> = {}
  const messages: string[] = []

  for (const [field, message] of checks) {
    const h = Number.parseFloat(hours[field] || '0') || 0
    const minHoursLimit = field === 'flexible' ? FLEXIBLE_MIN_HOURS_LIMIT : MIN_HOURS_LIMIT
    const isDurationViolation = h > MAX_HOURS_LIMIT || (h > 0 && h < minHoursLimit)
    if (isDurationViolation) {
      fieldErrors[field] = 'Revise'
      messages.push(
        field === 'flexible' && h > MAX_HOURS_LIMIT
          ? ValidationMessages.FLEXIBLE_DURATION
          : message
      )
    }
  }

  return { fieldErrors, messages }
}

/**
 * Builds the adjusted available balance map for each leave-type field.
 * In edit mode the hours already committed by OTHER dates are deducted;
 * otherwise the full available balance is returned so multi-date totals
 * are evaluated against the complete available pool.
 */
function buildAdjustedBalances(
  resultingBalances: Balance[],
  isEditMode: boolean,
  totalHoursByType: Record<string, number>
): Record<string, number | undefined> {
  return (['vacation', 'float', 'flexible', 'other'] as const).reduce((acc, f) => {
    const balanceItem = resultingBalances.find(b => b.id === f)
    const available = balanceItem?.available
    if (available && available !== 'N/A') {
      const hoursToDeduct = isEditMode ? (totalHoursByType[f] ?? 0) : 0
      acc[f] = Number.parseFloat(available) - hoursToDeduct
    } else {
      acc[f] = undefined
    }
    return acc
  }, Object.create(null) as Record<string, number | undefined>)
}

/**
 * Checks whether the total hours (per-day × date count) for a single field
 * exceeds the available balance when multiple dates are selected.
 * Mutates `nextFieldErrors` with 'Revise' and returns the banner message
 * string when a violation is found, or `null` otherwise.
 */
function checkMultiDateFieldBalance(
  fieldName: 'float' | 'flexible',
  perDayHours: number,
  dateCount: number,
  availableBalance: string | undefined,
  nextFieldErrors: Record<string, string>
): string | null {
  if (perDayHours <= 0 || !availableBalance || availableBalance === 'N/A') return null
  const totalHours = perDayHours * dateCount
  if (totalHours <= Number.parseFloat(availableBalance)) return null
  nextFieldErrors[fieldName] = 'Revise'
  if (fieldName === 'flexible') return ValidationMessages.FLEXIBLE_BALANCE
  if (fieldName === 'float') return ValidationMessages.FLOAT_BALANCE
  return null
}

/**
 * Accumulates multi-date balance errors for float and flexible fields.
 * Mutates `nextFieldErrors` in-place and returns banner messages to merge.
 */
function buildMultiDateBalanceErrors(
  hoursToValidate: HoursValues,
  dateCount: number,
  resultingBalances: Balance[],
  nextFieldErrors: Record<string, string>
): string[] {
  const errors: string[] = []
  ;(['float', 'flexible'] as const).forEach((fieldName) => {
    const perDayHours = Number.parseFloat(hoursToValidate[fieldName] || '0') || 0
    const availableBalance = resultingBalances.find(b => b.id === fieldName)?.available
    const err = checkMultiDateFieldBalance(fieldName, perDayHours, dateCount, availableBalance, nextFieldErrors)
    if (err) errors.push(err)
  })
  return errors
}


/**
 * Returns `true` when all non-birthday dates already have hours committed to
 * per-date storage, allowing form validation to be skipped on Next click.
 */
function allNonBirthdayDatesHaveStoredHours(
  selectedDates: string[],
  birthdayDate: string | null,
  perDateHoursStorage: Record<string, HoursValues>
): boolean {
  // Single-date, non-birthday request: check if that one date has stored hours
  if (selectedDates.length === 1 && birthdayDate === null) {
    return !!findByMatchingDate(perDateHoursStorage, selectedDates[0])
  }
  
  // Multi-date or birthday request: filter out birthday date (if any) and check all others have stored hours
  const nonBirthday = selectedDates.filter(d => d !== birthdayDate)
  return (
    nonBirthday.length > 0 &&
    nonBirthday.every((d) => !!findByMatchingDate(perDateHoursStorage, d))
  )
}

/**
 * Sums the numeric hours across every leave type in an {@link HoursValues}
 * record. Empty or non-numeric entries count as 0.
 *
 * @param hours The per-leave-type hours for a single date.
 * @returns The total hours for that date.
 */
function sumHoursValues(hours: HoursValues): number {
  return Object.values(hours).reduce(
    (sum, value) => sum + (Number.parseFloat(value || '0') || 0),
    0
  )
}

/**
 * Returns true when two HoursValues records are numerically equivalent.
 * Used to decide whether the latest typed ref should override stale edit state.
 */
function areHoursValuesEqual(a: HoursValues, b: HoursValues): boolean {
  return (
    (Number.parseFloat(a.vacation || '0') || 0) ===
      (Number.parseFloat(b.vacation || '0') || 0) &&
    (Number.parseFloat(a.float || '0') || 0) ===
      (Number.parseFloat(b.float || '0') || 0) &&
    (Number.parseFloat(a.flexible || '0') || 0) ===
      (Number.parseFloat(b.flexible || '0') || 0) &&
    (Number.parseFloat(a.other || '0') || 0) ===
      (Number.parseFloat(b.other || '0') || 0)
  )
}

/**
 * Returns `true` when a validation message is a spurious duration message
 * that should be suppressed — either a vacation message with no duration
 * violation, or a float message when 0 float hours are entered.
 */
function isSpuriousMessage(
  msg: string,
  vacationHours: number,
  floatHours: number,
  vacationDurationInvalid: boolean
): boolean {
  const suppressVacation = (!vacationDurationInvalid || vacationHours === 0) && msg.startsWith('Request for Vacation')
  const suppressFloat = floatHours === 0 && msg.startsWith('Request for Float')
  return suppressVacation || suppressFloat
}

/**
 * Removes the vacation field's inline error when the only cause is a balance
 * overage (not a genuine duration violation and not Required). Balance errors
 * are surfaced separately as soft warnings; the per-field marker must also be
 * cleared so the form's blocking behaviour stays consistent.
 *
 * `vacationDurationOrWindowInvalid` covers both ways vacation hours can be a
 * genuine duration problem: vacation's own 0.25-16 range, or the partial-day
 * total exceeding the selected time window (vacation can be individually
 * valid yet still be the field responsible for that overage).
 */
function suppressNonDurationVacationError(
  nextFieldErrors: Record<string, string>,
  vacationDurationOrWindowInvalid: boolean
): void {
  if (nextFieldErrors.vacation && nextFieldErrors.vacation !== 'Required' && !vacationDurationOrWindowInvalid) {
    delete nextFieldErrors.vacation
  }
}

/**
 * Returns the tooltip text for the birthday checkbox based on the current form state.
 * Extracted as a pure function to reduce the cognitive complexity of the component body.
 *
 * @param isBirthdayTaken - Whether the birthday leave has already been taken or is pending.
 * @param dateCount - The number of selected dates.
 * @param selectedDuration - The current duration selection ('Full Day' or 'Partial Day').
 * @param perDayRequested - Total hours entered across all leave types for the current day.
 * @returns A tooltip string explaining why the checkbox is in its current state.
 */
function getBirthdayCheckboxTooltip(
  isBirthdayTaken: boolean,
  dateCount: number,
  selectedDuration: string,
  perDayRequested: number,
): string {
  if (isBirthdayTaken) return 'Birthday has already been taken or is pending';
  if (dateCount > 1) return 'Birthday can only be requested for a single date';
  if (selectedDuration !== 'Full Day') return 'Birthday must be a full day';
  if (perDayRequested > 0) return 'Clear all hours to request birthday off';
  return 'Request birthday off';
}

/** Maximum hours allowed per day per leave type. */
const MAX_DURATION = 16;

const FLOAT_UNAVAILABLE_SENTINEL = "8888.88"

/**
 * Derives key validation metrics from the given hours object.
 * Pure function — contains no side effects and depends only on its arguments
 * and the module-level MAX_DURATION constant. Extracted to module level to
 * reduce the cognitive complexity of the AddTimeToRequest component body.
 *
 * @param hoursToCheck - The current leave-type hours to inspect.
 */
function getValidationConfig(hoursToCheck: HoursValues): {
  totalHoursAcrossAllTypes: number;
  vacationHoursEntered: number;
  vacationDurationInvalid: boolean;
  totalExceedsMaxDuration: boolean;
} {
  const totalHoursAcrossAllTypes = Object.values(hoursToCheck).reduce(
    (sum, hours) => sum + (Number.parseFloat(String(hours)) || 0),
    0
  );
  const vacationHoursEntered = Number.parseFloat(hoursToCheck.vacation || '0') || 0;
  const vacationDurationInvalid =
    vacationHoursEntered > MAX_DURATION ||
    (vacationHoursEntered > 0 && vacationHoursEntered < 0.25);
  const totalExceedsMaxDuration = totalHoursAcrossAllTypes > MAX_DURATION;
  return { totalHoursAcrossAllTypes, vacationHoursEntered, vacationDurationInvalid, totalExceedsMaxDuration };
}

/**
 * Filters the raw validation message list down to messages appropriate for the
 * Next/Update banner. Removes submit-only messages (EDUCATION_COMMENT_REQUIRED),
 * spurious duration messages that do not apply for the given hours, and the
 * BALANCE message (owned by the live-balance effect, never the Next banner).
 *
 * @param allMessages - Raw deduplicated validation messages.
 * @param hoursToValidate - The hours values used during this validation pass.
 * @returns Messages suitable for display in the Next/Update banner.
 */
function filterDisplayMessages(allMessages: string[], hoursToValidate: HoursValues): string[] {
  const vacationHours = Number.parseFloat(hoursToValidate.vacation || '0') || 0
  const floatHours = Number.parseFloat(hoursToValidate.float || '0') || 0
  const { vacationDurationInvalid } = getValidationConfig(hoursToValidate)
  return allMessages
    .filter(msg => msg !== ValidationMessages.EDUCATION_COMMENT_REQUIRED)
    .filter(msg => !isSpuriousMessage(msg, vacationHours, floatHours, vacationDurationInvalid))
    .filter(msg => msg !== ValidationMessages.BALANCE)
}

/**
 * Converts a plain string time window (`{ from: { hour, minute, ampm }, to: {…} }`)
 * into the {@link DropdownOption}-based {@link TimeFields} shape used by the
 * partial-day time pickers. Missing parts default to `1:00 AM`.
 *
 * @param timeData - The saved partial-day window with string hour/minute/ampm.
 * @returns The `{ from, to }` window shaped for the time-picker state.
 */
const toTimeFieldsWindow = (timeData: {
  from: { hour?: string; minute?: string; ampm?: string }
  to: { hour?: string; minute?: string; ampm?: string }
}): { from: TimeFields; to: TimeFields } => {
  const buildPart = (
    part: {
      hour?: string | { value?: string; label?: string }
      minute?: string | { value?: string; label?: string }
      ampm?: string | { value?: string; label?: string }
    }
  ): TimeFields => {
    const hour =
      typeof part.hour === 'string' ? part.hour : part.hour?.value ?? '1'
    const minute =
      typeof part.minute === 'string' ? part.minute : part.minute?.value ?? '00'
    const ampm =
      typeof part.ampm === 'string' ? part.ampm : part.ampm?.value ?? 'AM'
    return {
      hour: { id: `h-${hour}`, label: hour, value: hour },
      minute: { id: `m-${minute}`, label: minute, value: minute },
      ampm: { id: ampm?.toLowerCase(), label: ampm, value: ampm },
    }
  }
  return { from: buildPart(timeData.from), to: buildPart(timeData.to) }
}

/**
 * Resolves the Full/Partial Day duration (and any partial-day time window) to
 * hydrate for a single active edit date. Extracted to module level to keep the
 * hydration effect's cognitive complexity low.
 *
 * @param activeDate - The MM/DD/YYYY date currently being edited.
 * @param durationByDate - Saved Full/Partial durations keyed by MM/DD/YYYY.
 * @param timeByDate - Saved partial-day windows keyed by MM/DD/YYYY.
 * @returns The duration to apply plus an optional time window, or `null` when no
 *   duration map is supplied (i.e. the caller does not use this hydration path).
 */
const resolveHydratedDuration = (
  activeDate: string,
  durationByDate?: Record<string, 'Full Day' | 'Partial Day'>,
  timeByDate?: Record<string, { from: { hour?: string; minute?: string; ampm?: string }; to: { hour?: string; minute?: string; ampm?: string } }>
): { duration: 'Full Day' | 'Partial Day'; time?: { from: TimeFields; to: TimeFields } } | null => {
  if (!durationByDate) return null
  const savedDuration = durationByDate[activeDate]
  // A brand-new (not previously saved) date defaults to Full Day so it never
  // inherits a Partial Day setting from a previously edited date.
  if (!savedDuration) return { duration: 'Full Day' }
  if (savedDuration === 'Partial Day' && timeByDate?.[activeDate]) {
    return { duration: 'Partial Day', time: toTimeFieldsWindow(timeByDate[activeDate]) }
  }
  return { duration: savedDuration }
}

/**
 * Determines whether the user's selected partial-day window overlaps ANY of the
 * supplied other-user partial-day ranges for a single date. Extracted to module
 * level so {@link performPartialDayOverlapCheck} stays under the cognitive
 * complexity limit (the inner range loop was the deepest nesting there).
 *
 * @param userRange - The user's selected `[start, end]` window, in minutes.
 * @param otherRanges - Other-user partial-day ranges (ISO start/end strings).
 * @returns `true` when at least one valid range overlaps the user's window.
 */
const hasOverlappingPartialRange = (
  userRange: [number, number],
  otherRanges: Array<{ startTime?: string | null; endTime?: string | null }>
): boolean => {
  for (const otherPartial of otherRanges) {
    // Skip ranges missing either bound — they cannot define a comparable window.
    if (!otherPartial?.startTime || !otherPartial?.endTime) continue
    const otherStart = isoToUTCMinutes(otherPartial.startTime)
    const otherEnd = isoToUTCMinutes(otherPartial.endTime)
    if (doTimeRangesOverlap(userRange, [otherStart, otherEnd])) return true
  }
  return false
}



const AddTimeToRequest = ({ 
  selectedDates, 
  committedDates,
  onValidationError, 
  onCancelRequest,
  focusOtherSubtypeSignal,
  exitAddTimeReviewSignal,
  onAddTimeReviewChange,
  editMode = false,
  startInReview = false,
  isResubmitFlow = false,
  isBirthdayRequest = false,
  isVacationBidRequest = false,
  editDate,
  onEditDateClick,
  onRemoveDateClick,
  onExitEditMode,
  onBackFromEditMode,
  employeeId,
  employeeName,
  hideReviewTable,
  onReviewReady,
  initialPerDateHoursStorage,
  initialPerDateDurationStorage,
  initialPerDateTimeStorage,
  initialPerDateOtherSubTypeStorage,
  originalRequestId,
  initialComments,
  toReqId,
  initialHoursByDate,
  initialOtherSubTypeByDate,
  initialDurationByDate,
  initialTimeByDate,
  preservedDatesPayload,
  otherPartialDayDates,
  onRemoveDatesClick,
  isExempt = false,
  isAddingMoreDates = false,
  otherRequestHoursByDate,
  onAddMoreDates,
}: AddTimeToRequestProps) => {
  const loggedInEmployee = useSelector((state: RootState) => state.profile?.data?.employee ?? null)
  const loggedInUserName = [
    loggedInEmployee?.empFirstName,
    loggedInEmployee?.empMiddleName,
    loggedInEmployee?.empLastName,
  ]
    .filter(Boolean)
    .join(' ')
  const loggedInUserNuid = loggedInEmployee?.empNuid ?? ''

  // Use custom hook for all balance-related logic and state
  const {
    resultingBalances,
    hoursValues,
    setHoursValues,
    resetHours,
    isBirthdayEnabled,
    isBirthdayTaken,
  } = useTimeOffBalance()

  // ============= UI State =============
  const [isInDateEditMode, setIsInDateEditMode] = useState<boolean>(editMode);
  const [selectedDuration, setSelectedDuration] = useState<string>('Full Day');
  const [showHistoryTable, setShowHistoryTable] = useState<boolean>(startInReview || false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [isNextClicked, setIsNextClicked] = useState<boolean>(false);
  const vacationBalanceBannerActiveRef = useRef<boolean>(false)
  const lastValidationMessagesRef = useRef<string | null>(null)
  const [birthdayChecked, setBirthdayChecked] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');
  // Admin-only manual request received date
  const [adminReceivedDate, setAdminReceivedDate] = useState<string>('')
  const [adminReceivedDateError, setAdminReceivedDateError] = useState<string>('')
  const otherSubtypeDropdownRef = useRef<HTMLDivElement | null>(null)
  const previousOtherSubtypeSignalRef = useRef<number>(focusOtherSubtypeSignal ?? 0)
  const otherSubtypeHighlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isOtherSubtypeDropdownHighlighted, setIsOtherSubtypeDropdownHighlighted] = useState<boolean>(false)
  const previousEditDateRef = useRef<string | null>(null);
  useEffect(() => {
    return () => {
      if (otherSubtypeHighlightTimeoutRef.current) {
        clearTimeout(otherSubtypeHighlightTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const nextSignal = focusOtherSubtypeSignal ?? 0
    if (nextSignal === previousOtherSubtypeSignalRef.current) return
    previousOtherSubtypeSignalRef.current = nextSignal

    const container = otherSubtypeDropdownRef.current
    if (!container) return

    container.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const triggerButton = container.querySelector<HTMLButtonElement>('.ds-dropdown__trigger-button')
    if (triggerButton && !triggerButton.disabled) {
      triggerButton.focus()
    }

    setIsOtherSubtypeDropdownHighlighted(true)
    if (otherSubtypeHighlightTimeoutRef.current) {
      clearTimeout(otherSubtypeHighlightTimeoutRef.current)
    }
    otherSubtypeHighlightTimeoutRef.current = setTimeout(() => {
      setIsOtherSubtypeDropdownHighlighted(false)
    }, 1800)
  }, [focusOtherSubtypeSignal])

  // ============= Form Data State =============
  const [comments, setComments] = useState<string>('');
  const [commentError, setCommentError] = useState<string>('');
  const [leaveTypeSelected, setLeaveTypeSelected] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [hasEditsBeenMade, setHasEditsBeenMade] = useState<boolean>(false);
  const latestHoursValuesRef = useRef(hoursValues)
  useEffect(() => {
  latestHoursValuesRef.current = hoursValues
}, [hoursValues])

  // ============= Per-Date Hours Management =============
  // Stores custom hours for each specific date after editing
  const [perDateHoursStorage, setPerDateHoursStorage] = useState<Record<string, HoursValues>>({});
  
  // Stores duration selection per date
  const [perDateDurationStorage, setPerDateDurationStorage] = useState<Record<string, string>>({});
  
  // Stores time selections per date for partial days
  const [perDateTimeStorage, setPerDateTimeStorage] = useState<Record<string, { from: TimeFields; to: TimeFields }>>({});

  // Stores Other Types of Time Off subtype selection per date.
  // Ensures that editing the Other dropdown while in date-edit mode updates only that date,
  // not all selected dates.
  const [perDateOtherSubTypeStorage, setPerDateOtherSubTypeStorage] = useState<Record<string, string>>({});

  // currentEditingDateHours: holds hours being edited for the currently selected date
  // Used only when isInDateEditMode is true
  const [currentEditingDateHours, setCurrentEditingDateHours] = useState<HoursValues>({
    vacation: '0',
    float: '0',
    flexible: '0',
    other: '0',
  });

  // Resolves `editDate` (which can arrive as MM/DD/YYYY from the review
  // table's pencil-edit callback — see `onEditDate` in `ReviewTable`) to
  // whatever key format the per-date storage maps actually use internally
  // (normally YYYY-MM-DD, the raw `selectedDates` format). Falls back to
  // `editDate` itself when no matching stored key exists — i.e. this is a
  // genuinely new, not-yet-saved date, so there is nothing to resolve
  // against and `editDate` is already the key to write under.
  //
  // Centralizing this here — instead of the naive `storage[editDate]`
  // lookups previously scattered through this component — fixes reopening
  // an already-committed date via the review table's edit icon: without it,
  // every read/write against `perDateHoursStorage`/`perDateDurationStorage`/
  // `perDateTimeStorage`/`perDateOtherSubTypeStorage` missed under the
  // mismatched key, so the form reopened blank instead of showing the saved
  // hours/duration/time, and clicking Update wrote a second, disconnected
  // entry instead of updating the original one.
  const resolvedEditDate = useMemo(() => {
    if (!editDate) return editDate
    const candidateKeys = new Set<string>([
      ...selectedDates,
      ...Object.keys(perDateHoursStorage),
      ...Object.keys(perDateDurationStorage),
      ...Object.keys(perDateTimeStorage),
      ...Object.keys(perDateOtherSubTypeStorage),
    ])
    if (candidateKeys.has(editDate)) return editDate
    const match = Array.from(candidateKeys).find((key) => datesMatch(key, editDate))
    return match ?? editDate
  }, [editDate, selectedDates, perDateHoursStorage, perDateDurationStorage, perDateTimeStorage, perDateOtherSubTypeStorage])

  const getHoursForSpecificDate = (date: string): HoursValues => {
    return perDateHoursStorage[date] || hoursValues
  }

  /**
   * Calculate total hours (sum of all leave types) for a specific date
   * Used in review table to show correct hours for each date
   */
  const calculatePerDateTotalHours = (date: string): number => {
    const dateSpecificHours = getHoursForSpecificDate(date)
    return Object.values(dateSpecificHours).reduce(
      (sum, hoursValue) => sum + (Number.parseFloat(hoursValue) || 0),
      0
    )
  }

  // Sum the ACTUAL hours requested for each leave type across every selected
  // date. The date currently being edited contributes its live in-progress
  // hours; all other dates use their committed per-date hours (falling back to
  // the shared form hours for dates not yet individually saved). This yields the
  // true cumulative total (e.g. 8 + 8 + 2 = 18) instead of a single day's rate
  // multiplied by the date count, so editing one date's hours updates the
  // resulting balance correctly. Applies to full-day and partial-day leave alike.
  const totalRequestedHoursByType = useMemo<BalanceHoursTotals>(() => {
    const totals: BalanceHoursTotals = { vacation: 0, float: 0, flexible: 0, other: 0 }
    selectedDates.forEach((date) => {
      const isActiveEditDate = isInDateEditMode && datesMatch(editDate, date)
      const dateHours = isActiveEditDate
        ? currentEditingDateHours
        : (perDateHoursStorage[date] || hoursValues)
      ;(['vacation', 'float', 'flexible', 'other'] as const).forEach((type) => {
        totals[type] += Number.parseFloat(dateHours[type] || '0') || 0
      })
    })
    return totals
  }, [selectedDates, isInDateEditMode, editDate, currentEditingDateHours, perDateHoursStorage, hoursValues])

  // Resulting balance shown per leave type = available − the true cumulative
  // hours requested across all selected dates (see totalRequestedHoursByType).
  // This replaces the previous per-day × date-count formula, which mis-deducted
  // edited days by applying one day's hours to every selected date.
  const deductedBalances = useMemo(
    () => calculateResultingBalances(resultingBalances, totalRequestedHoursByType),
    [resultingBalances, totalRequestedHoursByType]
  )

  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine mode from URL path
  const isAdminMode = location.pathname.startsWith('/admin');
  
  const { submitDraft, submitFinal, submitResubmitted } = useCreateTimeOffRequest()

  // Reset hours and clear validation errors when selectedDates becomes empty
  useEffect(() => {
    if (!selectedDates || selectedDates.length === 0) {
      setFieldErrors({});
      lastValidationMessagesRef.current = null
      onValidationError?.(null);
      setShowHistoryTable(false);
      setIsNextClicked(false);
      resetHours();
      setPerDateOtherSubTypeStorage({});
    }
  }, [selectedDates, onValidationError]);

  /**
   * Clean up per-date storage entries when dates are removed from selectedDates.
   * This prevents newly added dates from inheriting values from previously selected dates.
   * Monitors selectedDates and removes any per-date storage entries for dates that are no longer selected.
   */
  useEffect(() => {
    if (!selectedDates || selectedDates.length === 0) return;

    // Get the current set of selected dates
    const currentDateSet = new Set(selectedDates);

    // Remove entries from per-date storage that are no longer selected
    setPerDateHoursStorage((prev) => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach((date) => {
        if (!currentDateSet.has(date)) {
          delete updated[date];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    setPerDateDurationStorage((prev) => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach((date) => {
        if (!currentDateSet.has(date)) {
          delete updated[date];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    setPerDateTimeStorage((prev) => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach((date) => {
        if (!currentDateSet.has(date)) {
          delete updated[date];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    setPerDateOtherSubTypeStorage((prev) => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach((date) => {
        if (!currentDateSet.has(date)) {
          delete updated[date];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });
  }, [selectedDates]);

  // Track if edits have been made (for resubmit flow)
  const initialStateRef = useRef<{
    comments: string;
    perDateHoursStorage: Record<string, HoursValues>;
    perDateDurationStorage: Record<string, string>;
    // Holds the raw API time format initially, then the converted DropdownOption format after
    // setPerDateTimeStorage runs — typed as unknown-valued map since both formats are used
    // only for JSON.stringify change-detection and never read with type expectations.
    perDateTimeStorage: Record<string, unknown>;
  } | null>(null);

  // Initialize per-date storage from props (used in EditSubmittedRequest)
  useEffect(() => {
    if (initialPerDateHoursStorage && Object.keys(initialPerDateHoursStorage).length > 0) {
      setPerDateHoursStorage(initialPerDateHoursStorage);
    }
    if (initialPerDateDurationStorage && Object.keys(initialPerDateDurationStorage).length > 0) {
      setPerDateDurationStorage(initialPerDateDurationStorage);
    }
    if (initialPerDateTimeStorage && Object.keys(initialPerDateTimeStorage).length > 0) {
      // Convert time strings to TimeFields objects
      const convertedTimeStorage: Record<string, { from: TimeFields; to: TimeFields }> = {};
      Object.entries(initialPerDateTimeStorage).forEach(([dateKey, timeData]) => {
        convertedTimeStorage[dateKey] = toTimeFieldsWindow(timeData);
      });
      setPerDateTimeStorage(convertedTimeStorage);
    }
    if (initialPerDateOtherSubTypeStorage && Object.keys(initialPerDateOtherSubTypeStorage).length > 0) {
      // Populate per-date storage so each date retains its own Other subtype on re-edit
      setPerDateOtherSubTypeStorage(initialPerDateOtherSubTypeStorage);
      // Also set a global fallback (first available value) for non-date-edit contexts
      const firstSubType = Object.values(initialPerDateOtherSubTypeStorage)[0];
      if (firstSubType) {
        setLeaveTypeSelected((prev) => ({ ...prev, other: firstSubType }));
      }
    }
    if (initialComments) {
      setComments(initialComments);
    }

    // Store initial state for edit tracking (only once on mount)
    if (isResubmitFlow && !initialStateRef.current) {
      initialStateRef.current = {
        comments: initialComments || '',
        perDateHoursStorage: initialPerDateHoursStorage || {},
        perDateDurationStorage: initialPerDateDurationStorage || {},
        perDateTimeStorage: initialPerDateTimeStorage || {},
      };
    }
  }, [initialPerDateHoursStorage, initialPerDateDurationStorage, initialPerDateTimeStorage, initialPerDateOtherSubTypeStorage, initialComments, isResubmitFlow]);

  // Update initialStateRef with converted perDateTimeStorage after conversion happens
  useEffect(() => {
    if (isResubmitFlow && initialStateRef.current && Object.keys(perDateTimeStorage).length > 0) {
      initialStateRef.current.perDateTimeStorage = perDateTimeStorage;
    }
  }, [perDateTimeStorage, isResubmitFlow]);

  // Track if any edits have been made (resubmit flow only)
  useEffect(() => {
    if (!isResubmitFlow || !initialStateRef.current) return;

    const hasCommentChanged = comments !== initialStateRef.current.comments;
    const hasHoursChanged = JSON.stringify(perDateHoursStorage) !== JSON.stringify(initialStateRef.current.perDateHoursStorage);
    const hasDurationChanged = JSON.stringify(perDateDurationStorage) !== JSON.stringify(initialStateRef.current.perDateDurationStorage);
    const hasTimeChanged = JSON.stringify(perDateTimeStorage) !== JSON.stringify(initialStateRef.current.perDateTimeStorage);

    setHasEditsBeenMade(hasCommentChanged || hasHoursChanged || hasDurationChanged || hasTimeChanged);
  }, [comments, perDateHoursStorage, perDateDurationStorage, perDateTimeStorage, isResubmitFlow]);

  // When entering edit mode for a date, load the pre-populated hours, duration, and time for that date
  useEffect(() => {
    if (isInDateEditMode && resolvedEditDate && perDateHoursStorage[resolvedEditDate]) {
      setCurrentEditingDateHours(perDateHoursStorage[resolvedEditDate]);
      if (perDateDurationStorage[resolvedEditDate]) {
        setSelectedDuration(perDateDurationStorage[resolvedEditDate]);
      }
      if (perDateTimeStorage[resolvedEditDate]) {
        setSelectedTime(perDateTimeStorage[resolvedEditDate]);
      }
    }
  }, [isInDateEditMode, resolvedEditDate, perDateHoursStorage, perDateDurationStorage, perDateTimeStorage]);

  // Sync editMode prop to isInDateEditMode state and clear banner when entering edit mode
  useEffect(() => {
    setIsInDateEditMode(editMode);
    // Clear validation banner when entering edit mode
    if (editMode) {
      onValidationError?.(null);
    }
  }, [editMode, onValidationError]);

  // Exempt employees (EMP_TYPE = 'S') can only request Full Day.
  // Reset to Full Day if a pre-populated Partial Day value slips in (e.g. from a
  // draft edit), so the form is always in a valid state for exempt employees.
  useEffect(() => {
    if (isExempt && selectedDuration === 'Partial Day') {
      setSelectedDuration('Full Day');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExempt]);

  // Hydrate the visible hour inputs when editing an existing request (e.g. a
  // DRAFT opened from DraftView). Without this the form starts at 0 and the
  // user's previously saved hours appear lost. We only seed the *visible*
  // `hoursValues` for the single active date — never `perDateHoursStorage` —
  // so the normal "Next/Update" flow still commits whatever the user finally
  // leaves in the inputs (avoiding a stale-storage override).
  //
  // The same single-active-date hydration also pre-fills the "Other" subtype
  // dropdown (`leaveTypeSelected.other`) so an Other-type selection persists on
  // re-edit instead of resetting to empty, and restores the saved Full/Partial
  // Day duration (plus its time window) so reopening a saved Partial Day date
  // never silently reverts it to Full Day.
  //
  // A ref tracks the last hydrated date so the effect acts only when the active
  // date actually changes; this prevents clobbering in-progress user edits when
  // the effect re-runs for unrelated reasons.
  const lastHydratedDateRef = useRef<string | null>(null)
  useEffect(() => {
    const activeDate = selectedDates.length === 1 ? selectedDates[0] : null
    if (!activeDate) {
      lastHydratedDateRef.current = null
      return
    }
    if (lastHydratedDateRef.current === activeDate) return

    lastHydratedDateRef.current = activeDate

    const sessionHours = perDateHoursStorage[activeDate]
    const savedHours = sessionHours ?? initialHoursByDate?.[activeDate]
    // Pre-fill from in-session per-date values first, then API values.
    // If neither exists, reset so brand-new dates never inherit prior inputs.
    setHoursValues(
      savedHours
        ? { ...savedHours }
        : { vacation: '0', float: '0', flexible: '0', other: '0' }
    )

    const sessionOtherSubType = perDateOtherSubTypeStorage[activeDate]
    const savedSubType =
      sessionOtherSubType ?? initialOtherSubTypeByDate?.[activeDate]
    // Pre-fill the saved Other subtype; clear it for dates without one so a
    // stale selection never leaks across dates in the same edit session.
    setLeaveTypeSelected((prev) => {
      const next = { ...prev }
      if (savedSubType) {
        next.other = savedSubType
      } else {
        delete next.other
      }
      return next
    })

    const sessionDuration = perDateDurationStorage[activeDate]
    const sessionTime = perDateTimeStorage[activeDate]

    if (sessionDuration) {
      setSelectedDuration(sessionDuration)
      if (sessionDuration === 'Partial Day' && sessionTime) {
        setSelectedTime(sessionTime)
      } else {
        // Full Day or missing session time: reset the partial-day pickers.
        setSelectedTime(DEFAULT_PARTIAL_TIME_WINDOW)
      }
      return
    }

    // Fallback to API-backed maps when there is no in-session duration/time.
    const hydrated = resolveHydratedDuration(
      activeDate,
      initialDurationByDate,
      initialTimeByDate
    )
    if (hydrated) {
      setSelectedDuration(hydrated.duration)
      if (hydrated.time) {
        setSelectedTime(hydrated.time)
      } else {
        // Brand-new date with no saved partial-day window (resolveHydratedDuration
        // returned { duration: 'Full Day' } with no time). Reset the pickers to
        // defaults so stale From/To values from the previously viewed date are
        // never shown when the user manually switches to Partial Day on this date.
        setSelectedTime(DEFAULT_PARTIAL_TIME_WINDOW)
      }

    }

    // No session or API duration info: enforce clean defaults.
  
  }, [
    initialHoursByDate,
    initialOtherSubTypeByDate,
    initialDurationByDate,
    initialTimeByDate,
    selectedDates,
    perDateHoursStorage,
    perDateOtherSubTypeStorage,
    perDateDurationStorage,
    perDateTimeStorage,
    setHoursValues,
  ])

  // Watch for a parent-triggered request to exit review mode
  useEffect(() => {
    if (typeof exitAddTimeReviewSignal === 'number' && exitAddTimeReviewSignal > 0) {
      if (showHistoryTable) {
        setShowHistoryTable(false)
        setIsNextClicked(false)
        setFieldErrors({})

        // Reopening an already-committed date (e.g. clicking the review
        // table's pencil icon, which also bumps this signal) must preserve
        // its saved hours/duration/time — the hydration effect below
        // (`isInDateEditMode && editDate && perDateHoursStorage[editDate]`)
        // is responsible for restoring them. Without this guard, the
        // unconditional reset further down (meant only for a genuinely new,
        // not-yet-saved date) ran AFTER that hydration effect on the same
        // render and clobbered the just-restored values back to blank
        // defaults, so reopening a date with 8 hours saved showed 0.
        const isReopeningExistingDate =
          !!resolvedEditDate &&
          (!!perDateHoursStorage[resolvedEditDate] || !!perDateDurationStorage[resolvedEditDate])
        if (isReopeningExistingDate) {
          return
        }

        // Req 3 cross-date preselection: if there are committed partial-day dates in
        // storage, carry their times forward so the next date auto-populates with the
        // same partial selection instead of resetting to Full Day.
        // Reading perDateDurationStorage / perDateTimeStorage from the closure is safe
        // here — they hold the committed state from the most recent Update/Next click.
        const partialEntry = Object.entries(perDateDurationStorage).find(
          ([, dur]) => dur === 'Partial Day'
        );
        if (partialEntry && perDateTimeStorage[partialEntry[0]]) {
          // Carry the committed partial-day time window forward for convenience.
          // New dates must start fresh — do not inherit duration/time from a
          // previously committed partial-day date.
          setSelectedDuration('');
          setSelectedTime(DEFAULT_PARTIAL_TIME_WINDOW);
          // Bug fix: a newly added date must NOT inherit the previous date's data.
          // The committed dates keep their values in the per-date storage maps, so
          // blanking these shared globals only affects NEW dates. The review table
          // (getRequestTypeForDate) and the "Other" subtype dropdown fall back to
          // hoursValues / leaveTypeSelected / currentEditingDateHours for dates not
          // yet in per-date storage — leaving them stale is what made a new partial
          // date show the prior date's hours, leave type, and Other subtype. The
          // Full Day branch already resets these; do the same here (minus the
          // duration reset, since we intentionally keep Partial Day + time above).
          setHoursValues({ vacation: '0', float: '0', flexible: '0', other: '0' });
          setCurrentEditingDateHours({ vacation: '0', float: '0', flexible: '0', other: '0' });
          setLeaveTypeSelected({});
          resetOnNextEditDateRef.current = true
          // New-date entries should not inherit any prior partial selection.
        } else {
          // No committed partials — perform the standard full reset so the next
          // request starts with a clean slate.
          resetOnNextEditDateRef.current = true
          setHoursValues({ vacation: '0', float: '0', flexible: '0', other: '0' })
          setLeaveTypeSelected({})
          setSelectedDuration('Full Day')
          setSelectedTime(DEFAULT_PARTIAL_TIME_WINDOW)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitAddTimeReviewSignal]);

  // ─── Edit-mode useEffect branch helpers ───────────────────────────────────
  // Each helper handles one mutually-exclusive branch of the editMode/editDate
  // useEffect.  Extracting them reduces that effect's cognitive complexity from
  // 31 → ≤6 while keeping the logic readable.

  /** Loads saved hours, duration, time, and Other subtype for a date that already exists in per-date storage. */
  const applyExistingDateEditState = (date: string): void => {
    setCurrentEditingDateHours(perDateHoursStorage[date]);
    setSelectedDuration(perDateDurationStorage[date] || 'Full Day');
    if (perDateTimeStorage[date]) {
      setSelectedTime(perDateTimeStorage[date]);
    }
    // Hydrate the saved Other subtype so the dropdown shows the per-date selection,
    // not the global value. This fixes HRCOM-105805: when editing multiple dates
    // with different Other subtypes, each date must show its own saved selection.
    if (perDateOtherSubTypeStorage[date]) {
      setLeaveTypeSelected((prev) => ({ ...prev, other: perDateOtherSubTypeStorage[date] }));
    } else {
      // No saved Other subtype for this date — clear it so the dropdown is empty
      // and doesn't inherit from another date in the same session.
      setLeaveTypeSelected((prev) => {
        const next = { ...prev };
        delete next.other;
        return next;
      });
    }
  };

  /**
   * Handles a brand-new date that was added right after the user exited review
   * (resetOnNextEditDateRef is true). Resets the flag, then either forces
   * Partial Day (half-circle date) or fully resets to Full Day defaults.
   */
  const applyNewDateWithResetFlagState = (date: string): void => {
    resetOnNextEditDateRef.current = false;
    if (otherPartialDayDates?.[date]) {
      const committedEntry = selectedDates.find(d => d !== date && perDateHoursStorage[d]);
      if (committedEntry) {
        setHoursValues(perDateHoursStorage[committedEntry]);
        setCurrentEditingDateHours(perDateHoursStorage[committedEntry]);
      } else {
        setCurrentEditingDateHours({ vacation: '0', float: '0', flexible: '0', other: '0' });
      }
      setCurrentEditingDateHours({ vacation: '0', float: '0', flexible: '0', other: '0' });
      setSelectedDuration('Partial Day');
      setSelectedTime(DEFAULT_PARTIAL_TIME_WINDOW)
      setLeaveTypeSelected({});
      setPerDateDurationStorage(prev => {
        const updated = { ...prev };
        selectedDates.forEach(d => {
          if (
            d !== date &&
            !committedDates?.includes(d) &&
            !perDateHoursStorage[d] &&
            !initialPerDateHoursStorage?.[d] &&
            updated[d] === 'Full Day'
          ) {
            updated[d] = 'Partial Day';
          }
        });
        return updated;
      });
    } else {
      setHoursValues({ vacation: '0', float: '0', flexible: '0', other: '0' });
      setCurrentEditingDateHours({ vacation: '0', float: '0', flexible: '0', other: '0' });
      setSelectedDuration(otherPartialDayDates?.[date] ? 'Partial Day' : 'Full Day');
      setSelectedTime(DEFAULT_PARTIAL_TIME_WINDOW)
      setLeaveTypeSelected({});
    }
  };

  /**
   * Handles a brand-new date added during an active edit session where the date
  * has another user's partial-day request (half-circle). Forces Partial Day and
  * converts only in-progress (non-committed) Full Day dates to Partial Day.
  * A date already holding saved per-date hours is treated as committed even
  * without a `committedDates` entry — it was confirmed via Next/Update earlier
  * in this same session and must not be silently flipped to Partial Day.
  * Checks both `perDateHoursStorage` (state, current after a prior render) and
  * `initialPerDateHoursStorage` (prop, current within this same mount's effect
  * batch before that seeding effect's setState has actually landed) since the
  * mount-time race between the seeding effect and this one would otherwise read
  * `perDateHoursStorage` as still empty.
   */
  const applyPartialDayActiveSessionState = (date: string): void => {
    setHoursValues({ vacation: '0', float: '0', flexible: '0', other: '0' });
    setCurrentEditingDateHours({ vacation: '0', float: '0', flexible: '0', other: '0' });
    setSelectedDuration('Partial Day');
    setSelectedTime(DEFAULT_PARTIAL_TIME_WINDOW)
    setLeaveTypeSelected({});
    setPerDateDurationStorage(prev => {
      const updated = { ...prev };
      selectedDates.forEach(d => {
        if (
          d !== date &&
          !committedDates?.includes(d) &&
          !perDateHoursStorage[d] &&
          !initialPerDateHoursStorage?.[d] &&
          updated[d] === 'Full Day'
        ) {
          updated[d] = 'Partial Day';
        }
      });
      return updated;
    });
  };

  /**
   * Handles a brand-new date added during an active edit session with no
   * half-circle constraint. Carries over the Partial Day duration from any
   * committed partial-day date, but always resets the From/To time fields to
   * defaults so the new date never inherits another date's time window.
   */
  const applyNormalActiveSessionState = (date: string): void => {
    setHoursValues({ vacation: '0', float: '0', flexible: '0', other: '0' });
    setCurrentEditingDateHours({ vacation: '0', float: '0', flexible: '0', other: '0' });
    const partialEntry = Object.entries(perDateDurationStorage).find(
      ([d, dur]) => d !== date && dur === 'Partial Day'
    );
    if (partialEntry && perDateTimeStorage[partialEntry[0]]) {
      setSelectedDuration('Partial Day');
    } else {
      setSelectedDuration('Full Day');
    }
    // Always reset From/To time for a brand-new date — never inherit another date's time window
    setSelectedTime(DEFAULT_PARTIAL_TIME_WINDOW);
    setLeaveTypeSelected({});
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Sync edit mode state: when entering edit mode, load hours, duration, and time for the selected date
  useEffect(() => {
    setIsInDateEditMode(editMode || false);
    // Use resolvedEditDate (not the raw editDate prop) for every per-date
    // storage lookup/key below — editDate can arrive as MM/DD/YYYY from the
    // review table's edit icon while storage is keyed by the raw
    // selectedDates format (normally YYYY-MM-DD). resolvedEditDate falls
    // back to editDate itself when nothing matches, so this is a no-op for
    // genuinely new dates and only changes behavior for the mismatched case.
    if (editMode && resolvedEditDate) {
      // Issue (HRCOM-104959): clear any stale validation error and banner left
      // over from the previously edited date. Switching to a different date must
      // present a clean slate so a "Required"/"no hours" error from one date
      // never persists while the user works on another. Resetting isNextClicked
      // also releases the click-gated inline markers for the new date.
      setIsNextClicked(false);
      setFieldErrors({});
      lastValidationMessagesRef.current = null;
      onValidationError?.(null);

      // Brand-new dates (not yet in per-date storage) must start with fresh defaults
      // so the form doesn't inherit the global form values from other dates.
      // Brand-new dates (not yet in per-date storage) must start with fresh defaults
// only for the first post-review new date. Subsequent new-date switches should
// carry in-progress inputs until Next/Update is clicked.
const isNewDate = !perDateHoursStorage[resolvedEditDate];

if (!isNewDate) {
  applyExistingDateEditState(resolvedEditDate);
} else if (resetOnNextEditDateRef.current) {
  applyNewDateWithResetFlagState(resolvedEditDate);
} else if (otherPartialDayDates?.[resolvedEditDate]) {
  applyPartialDayActiveSessionState(resolvedEditDate);
} else {
  const previousEditDate = previousEditDateRef.current;
  const switchedFromUnsavedNewDate =
    !!previousEditDate && !perDateHoursStorage[previousEditDate];

  if (switchedFromUnsavedNewDate) {
    // Carry the same in-progress inputs when hopping between unsaved new dates.
    setHoursValues(currentEditingDateHours);

    const previousOtherSubtype = perDateOtherSubTypeStorage[previousEditDate] ?? leaveTypeSelected.other;
    setLeaveTypeSelected((prev) => {
      const next = { ...prev };
      if (previousOtherSubtype) {
        next.other = previousOtherSubtype;
      } else {
        delete next.other;
      }
      return next;
    });
    // keep selectedDuration / selectedTime / currentEditingDateHours as-is
  } else {
    // First brand-new date after leaving review/editing committed date: reset.
    applyNormalActiveSessionState(resolvedEditDate);
  }
}
previousEditDateRef.current = resolvedEditDate ?? null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, resolvedEditDate]);

  // Inform parent when review state changes
  useEffect(() => {
    onAddTimeReviewChange?.(showHistoryTable)
  }, [showHistoryTable, onAddTimeReviewChange])


  // Track previous selectedDates length and review state
  const previousDateCountRef = useRef<number>(selectedDates.length)
  const previousShowHistoryTableRef = useRef<boolean>(showHistoryTable)
  const wasBirthdayRef = useRef<boolean>(false)
  const isBirthdayRequestRef = useRef<boolean>(false) // Persistent flag: true if ever marked as birthday
  const originalBirthdayDateRef = useRef<string | null>(null)
  const originalBirthdayRequestTypeRef = useRef<string | null>(null)
  // Set to true when transitioning out of review (first edit date after Next should reset; subsequent should retain).
  const resetOnNextEditDateRef = useRef<boolean>(false)
  // Set when a date is removed from edit mode. The very next newly added date
  // must initialize from clean defaults instead of inheriting remaining-date values.
  const forceFreshInitOnNextAddRef = useRef<boolean>(false)
  // Always-current snapshot of hoursValues — updated every render so the date-count
  // effect can read the hours that were active at the moment a new date was added,
  // without needing hoursValues in the effect's deps array (which would cause it to
  // fire on every keystroke).
  const hoursValuesRef = useRef(hoursValues)
  hoursValuesRef.current = hoursValues

  // Initialize birthday checkbox from prop if this is a resubmit of a birthday request
  useEffect(() => {
    if (isResubmitFlow && isBirthdayRequest) {
      setBirthdayChecked(true)
      isBirthdayRequestRef.current = true
      originalBirthdayRequestTypeRef.current = 'Birthday'
      // Initialize ref to first date for birthday requests
      if (selectedDates.length > 0) {
        originalBirthdayDateRef.current = selectedDates[0]
      }
    }
  }, [isResubmitFlow, isBirthdayRequest, selectedDates])

  // Reset form inputs when new dates are added
  // 1. Continuous selection (no review page visited): retain form values so the same hours
  //    and Other Types selection carry across all selected dates; clear only transient UI state.
  // 2. After review page (new separate request): clear ALL storage and reset every form field
  //    so the next request starts completely fresh.
  // Extracted from the effect below (rather than inlined) purely to keep the
  // effect's own Cognitive Complexity under Sonar's threshold — behavior is
  // unchanged, this is the exact same branching that used to run inline.
  const applyDatesAddedTransition = (
    currentCount: number,
    previousCount: number,
    wasInReview: boolean,
    isNowInReview: boolean
  ): void => {
    if (currentCount <= previousCount) return

    const wasEditRemoveFlow = forceFreshInitOnNextAddRef.current
    // Reset only when transitioning from review -> form after a completed Next.
    // Do not reset during pre-review continuous date selection.
    const isPostReviewAddFlow = wasInReview && !isNowInReview && !isResubmitFlow

    if (isPostReviewAddFlow || wasEditRemoveFlow) {
      forceFreshInitOnNextAddRef.current = false
      resetOnNextEditDateRef.current = true
      resetHours()
      setCurrentEditingDateHours({ vacation: '0', float: '0', flexible: '0', other: '0' })
      setSelectedDuration('Full Day')
      setSelectedTime(DEFAULT_PARTIAL_TIME_WINDOW)
      setLeaveTypeSelected({})
      setIsNextClicked(false)
      setBirthdayChecked(false)
      setFieldErrors({})
      onValidationError?.(null)
      return
    }

    // PRE-REVIEW CONTINUOUS SELECTION: validate duration only.
    // Do NOT reset hours — retain form values so all continuously-selected
    // dates share the same entered hours without re-typing.
    const { fieldErrors: durationErrors, messages: durationMessages } = findDurationViolations(hoursValuesRef.current)

    if (Object.keys(durationErrors).length > 0) {
      // Duration violation: keep hours visible, show error banner.
      setFieldErrors(durationErrors)
      onValidationError?.(durationMessages)
      setIsNextClicked(true)
      scrollToTopAndFocusAlert()
      setBirthdayChecked(false)
      return
    }

    // Valid hours, pre-review: retain hours/duration/leaveType.
    // Only clear transient UI state (birthday toggle, errors, banner).
    setBirthdayChecked(false)
    setFieldErrors({})
    onValidationError?.(null)
  }

  // If the tracked birthday date is no longer selected, the user deselected the
  // birthday date itself (e.g. checked birthday on date 1, added date 2, then
  // removed date 1). Clear all birthday tracking so its 'Birthday' type cannot
  // bleed onto whichever date remains. This is deliberately keyed on the birthday
  // date leaving the selection — NOT on birthdayChecked — because birthday is
  // auto-unchecked for multi-date requests while the birthday date is still valid.
  const clearBirthdayTrackingIfDateRemoved = (): void => {
    if (
      !isBirthdayRequestRef.current ||
      !originalBirthdayDateRef.current ||
      selectedDates.includes(originalBirthdayDateRef.current)
    ) {
      return
    }

    // Capture the deselected birthday date before clearing so its stale zero-hours
    // per-date storage (written when the request became multi-date) can be purged.
    // Otherwise, re-selecting that date later would be treated as already having
    // committed hours (allNonBirthdayDatesHaveStoredHours only checks presence, not
    // value), skipping the mandatory "enter hours for full day" requirement.
    const removedBirthdayDate = originalBirthdayDateRef.current
    originalBirthdayDateRef.current = null
    originalBirthdayRequestTypeRef.current = null
    isBirthdayRequestRef.current = false
    const purgeDateEntry = <T,>(store: Record<string, T>): Record<string, T> => {
      if (!(removedBirthdayDate in store)) return store
      const rest = { ...store }
      delete rest[removedBirthdayDate]
      return rest
    }
    setPerDateHoursStorage(purgeDateEntry)
    setPerDateDurationStorage(purgeDateEntry)
    setPerDateTimeStorage(purgeDateEntry)
    setPerDateOtherSubTypeStorage(purgeDateEntry)
  }

  // Track the original birthday date only for active birthday flow.
  // Non-birthday draft edits must not overwrite this ref, otherwise
  // date-switching can incorrectly purge per-date storage.
  const captureBirthdayDateIfSingleSelection = (currentCount: number): void => {
    if (
      !birthdayChecked ||
      currentCount !== 1 ||
      selectedDates.length === 0 ||
      isResubmitFlow
    ) {
      return
    }
    originalBirthdayDateRef.current = selectedDates[0]
  }

  useEffect(() => {
    const previousCount = previousDateCountRef.current
    const currentCount = selectedDates.length
    const wasInReview = previousShowHistoryTableRef.current
    const isNowInReview = showHistoryTable

    // Case: New dates were added
    applyDatesAddedTransition(currentCount, previousCount, wasInReview, isNowInReview)

    // Track if birthday was checked before this render
    const birthdayJustChanged = birthdayChecked && !wasBirthdayRef.current
    wasBirthdayRef.current = birthdayChecked

    clearBirthdayTrackingIfDateRemoved()
    captureBirthdayDateIfSingleSelection(currentCount)

    // Save birthday request type when birthday checkbox is checked
    if (birthdayJustChanged && !isResubmitFlow) {
      originalBirthdayRequestTypeRef.current = 'Birthday'
    }

    previousDateCountRef.current = currentCount
    previousShowHistoryTableRef.current = isNowInReview
  }, [selectedDates.length, showHistoryTable, isInDateEditMode, resetHours, onValidationError, birthdayChecked])

  // Clear stale validation when the SET of selected dates changes without adding
  // a date — e.g. the user swaps one selected date for another (count stays the
  // same) or removes a date. The date-count effect above is keyed on
  // `selectedDates.length` and only acts on a count increase, so a same-count
  // swap previously left the prior date's inline field error and banner on
  // screen until the next click (HRCOM-104959). Count increases are deliberately
  // left to that effect so its duration-violation retention for continuous
  // multi-date selection is preserved. Switching the active edit date (same set,
  // different `editDate`) is handled by the edit-mode sync effect instead.
  const selectedDatesKey = [...selectedDates]
    .sort((a, b) => a.localeCompare(b))
    .join('|')
  const previousSelectedDatesKeyRef = useRef<string>(selectedDatesKey)
  useEffect(() => {
    const previousKey = previousSelectedDatesKeyRef.current
    previousSelectedDatesKeyRef.current = selectedDatesKey
    if (previousKey === selectedDatesKey) return

    // The selection changed: release the "validation is current" flag so any
    // click-gated inline marker (no-hours 'Required', Other-subtype required)
    // from the previous selection stops rendering immediately. This runs for
    // every change — including count increases — because it only affects
    // click-gated markers, never the duration errors the date-count effect
    // re-applies for continuous multi-date selection (HRCOM-104959).
    setIsNextClicked(false)

    const previousCount = previousKey ? previousKey.split('|').length : 0
    const currentCount = selectedDatesKey ? selectedDatesKey.split('|').length : 0
    // Count increases are handled (with duration retention) by the date-count effect.
    if (currentCount > previousCount) return

    // Swap or removal: drop the previous selection's stale inline error and
    // banner so the next Next/Update click re-evaluates the new selection
    // cleanly. Release the live vacation-balance banner ref too, so its effect
    // can re-show a fresh overage for the new selection if one still applies.
    setFieldErrors({})
    lastValidationMessagesRef.current = null
    vacationBalanceBannerActiveRef.current = false
    onValidationError?.(null)
  }, [selectedDatesKey, onValidationError])

  // After birthday-to-multi-date conversion: persist entered hours to storage for the original date.
  // This ensures hours are saved for editing and review.
  // IMPORTANT: This effect is intentionally scoped to birthday flows (wasBirthdayRef.current).
  // In regular multi-date selection the form values are retained in-place (not committed to
  // perDateHoursStorage) so they remain editable until the user explicitly clicks "Next".
  // Saving to storage here for non-birthday flows would lock in whatever hours are currently
  // in the form — including potentially invalid values — bypassing Next-click validation.
  useEffect(() => {
    const birthdayDate = originalBirthdayDateRef.current
    if (selectedDates.length > 1 && birthdayDate && wasBirthdayRef.current) {
      // Birthday date always gets zero hours — birthday leave is a fixed type with no
      // user-entered hours. Storing hoursValues here would cause the birthday date to
      // show vacation/float hours in the review table instead of 0.
      const zeroHours: HoursValues = { vacation: '0', float: '0', flexible: '0', other: '0' }
      setPerDateHoursStorage((prev) => ({
        ...prev,
        [birthdayDate]: zeroHours,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates.length])

  // Vacation-only live BALANCE banner on the first screen.
  // Shows immediately when vacation overages happen, scrolls to top once per
  // transition, and clears on resolve or when moving to review screen.
  useEffect(() => {
    const onFirstScreen = !showHistoryTable
    const activeHours = isInDateEditMode ? currentEditingDateHours : hoursValues
    const validationConfig = getValidationConfig(activeHours)

    let shouldShowVacationBalanceBanner = false
    if (onFirstScreen && validationConfig.vacationHoursEntered > 0) {
      // Deduct the true cumulative vacation hours across every date (the edited
      // date included) so the overage banner stays in sync with the resulting
      // balance display rather than using a per-day × date-count estimate.
      const deducted = calculateResultingBalances(resultingBalances, totalRequestedHoursByType)
      shouldShowVacationBalanceBanner =
        deducted.vacation !== 'N/A' && Number.parseFloat(deducted.vacation) < 0
    }

    const hasExplicitValidationBanner = lastValidationMessagesRef.current !== null

    if (shouldShowVacationBalanceBanner) {
      // Always show the banner when vacation balance is negative
      // Scroll to top only on first transition (when ref is false)
      if (!vacationBalanceBannerActiveRef.current) {
        scrollToTopAndFocusAlert()
      }
      vacationBalanceBannerActiveRef.current = true
      // Do not overwrite a blocking banner emitted by explicit Next/Update
      // validation (e.g. duration/hours errors).
      if (!hasExplicitValidationBanner) {
        onValidationError?.([ValidationMessages.BALANCE])
      }
    } else if (!shouldShowVacationBalanceBanner && vacationBalanceBannerActiveRef.current) {
      // Only clear when transitioning from active to inactive
      vacationBalanceBannerActiveRef.current = false
      // Do not wipe an explicit Next/Update validation banner that was just
      // emitted from validateAllHours.
      if (lastValidationMessagesRef.current === null) {
        onValidationError?.(null)
      }
    }
  }, [
    showHistoryTable,
    isInDateEditMode,
    currentEditingDateHours,
    hoursValues,
    totalRequestedHoursByType,
    resultingBalances,
    onValidationError,
  ])

  const otherLeaveOptions: DropdownOption[] = [
    { id: 'OTH_JD', label: 'Jury Duty', value: 'OTH_JD' },
    { id: 'OTH_UB', label: 'Union Business', value: 'OTH_UB' },
    { id: 'OTH_BL', label: 'Bereavement Leave', value: 'OTH_BL' },
    { id: 'OTH_TM', label: 'Training/Meetings', value: 'OTH_TM' },
    { id: 'OTH_ET', label: 'Education Time', value: 'OTH_ET' },
    { id: 'OTH_DA', label: 'Doctors Appointment', value: 'OTH_DA' },
    { id: 'OTH_UT', label: 'Unpaid Time', value: 'OTH_UT' },
    { id: 'OTH_LT', label: 'Labor Management Partnership Time', value: 'OTH_LT' },
  ];

  // Format and sort dates (keep as raw dates for consistency)
  const sortedDates = [...selectedDates].sort((a, b) => {
    return new Date(a).getTime() - new Date(b).getTime();
  });

  const sortedCommittedDates = [...selectedDates]
    .filter((date) => !!committedDates?.includes(date))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

  const sortedCurrentlyAddingDates = [...selectedDates]
    .filter((date) => !committedDates?.includes(date))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

  const handleDurationChange = (value: string) => {
    setSelectedDuration(value);
    // Reset field errors and banner messages when duration changes
    setFieldErrors({});
    onValidationError?.(null);
    // Auto-uncheck birthday when switching to Partial Day
    // Birthday is only allowed for Full Day
    if (value === 'Partial Day' && birthdayChecked) {
      setBirthdayChecked(false);
    }
    // Req 3 deselect: a date that is in otherPartialDayDates (another user's partial,
    // shown as a half-circle) can only be used for a new partial-day request in THIS
    // request. If the user switches the form to Full Day, those half-circle dates are
    // no longer eligible — deselect them entirely (as if the user clicked to uncheck
    // them in the calendar). All other selected dates remain unchanged.
    // Guard: only applies in the create flow (not resubmit/edit-submitted).
    if (value === 'Full Day' && !isResubmitFlow && !startInReview) {
      // Keep previously committed review rows untouched; only remove currently
      // add-flow partial-day dates that are incompatible with Full Day.
      const partialDatesToRemove = selectedDates.filter(
        (d) => !!otherPartialDayDates?.[d] && !committedDates?.includes(d)
      );
      if (partialDatesToRemove.length > 0) {
        if (onRemoveDatesClick) {
          onRemoveDatesClick(partialDatesToRemove);
        } else {
          partialDatesToRemove.forEach(d => onRemoveDateClick?.(d));
        }
        // Clean up per-date storage for the removed half-circle dates.
        setPerDateDurationStorage(prev => {
          const updated = { ...prev };
          partialDatesToRemove.forEach(d => { delete updated[d]; });
          return updated;
        });
        setPerDateTimeStorage(prev => {
          const updated = { ...prev };
          partialDatesToRemove.forEach(d => { delete updated[d]; });
          return updated;
        });
        setPerDateHoursStorage(prev => {
          const updated = { ...prev };
          partialDatesToRemove.forEach(d => { delete updated[d]; });
          return updated;
        });
      }
    }
  };


  // Partial-day time selection state (object-based):
  // `selectedTime` stores values for `from` and `to` as { hour, minute, ampm }
  const [selectedTime, setSelectedTime] = useState<{ from: TimeFields; to: TimeFields }>({
    ...DEFAULT_PARTIAL_TIME_WINDOW,
  });

  // Initialize selectedTime from first prepopulated date in resubmit flow
  useEffect(() => {
    if (isResubmitFlow && startInReview && selectedDates.length > 0 && Object.keys(perDateTimeStorage).length > 0) {
      // When the user is in edit mode, the edit-mode sync effect (applyExistingDateEditState /
      // applyNormalActiveSessionState) is responsible for setting selectedTime for the specific
      // date being edited. Firing here would re-apply the first date's committed partial-day
      // window and override the edit-mode reset — producing stale From/To values on new dates.
      if (editMode) return;
      // Get the first date's time
      const firstDate = selectedDates[0];
      const firstDateTime = perDateTimeStorage[firstDate];
      if (firstDateTime) {
        setSelectedTime(firstDateTime);
      }
    }
  }, [isResubmitFlow, startInReview, selectedDates, perDateTimeStorage, editMode]);

  useEffect(() => {
  if (isInDateEditMode && resolvedEditDate) {
    const savedDuration = perDateDurationStorage[resolvedEditDate]

    if (savedDuration) {
      setSelectedDuration(savedDuration)
    }

    const savedTime = perDateTimeStorage[resolvedEditDate]

    if (savedTime) {
      setSelectedTime(savedTime)
    }
  }
}, [
  isInDateEditMode,
  resolvedEditDate,
  perDateDurationStorage,
  perDateTimeStorage
])

  // ============= Birthday Constraint Effects =============
  // True while the user is editing the ORIGINAL birthday date within a
  // multi-date request. In this context the birthday remains valid even though
  // the overall request has several dates, so the multi-date rules that
  // normally uncheck/disable birthday must NOT apply to it.
  const isEditingBirthdayDate =
    isInDateEditMode &&
    isBirthdayRequestRef.current &&
    !!editDate &&
    !!originalBirthdayDateRef.current &&
    datesMatch(editDate, originalBirthdayDateRef.current)

  // Auto-uncheck and disable birthday when multiple dates selected
  // Birthday is only allowed for single date requests — except when editing the
  // original birthday date, where it must stay checked (fixed full-day type).
  useEffect(() => {
    if (selectedDates.length > 1 && birthdayChecked && !isEditingBirthdayDate) {
      setBirthdayChecked(false);
    }
  }, [selectedDates.length, birthdayChecked, isEditingBirthdayDate]);

  // Re-check birthday when the user opens the original birthday date for editing
  // inside a multi-date request. This runs ONLY on the transition into editing
  // that date (rising edge) so the checkbox is restored once; afterwards the
  // user is free to uncheck it to convert the date into a normal hours entry.
  const wasEditingBirthdayDateRef = useRef<boolean>(false)
  useEffect(() => {
    if (isEditingBirthdayDate && !wasEditingBirthdayDateRef.current) {
      setBirthdayChecked(true);
    }
    wasEditingBirthdayDateRef.current = isEditingBirthdayDate;
  }, [isEditingBirthdayDate]);

  // Stable, whole-session flag: was the date opened for editing the birthday
  // date? Unlike `isEditingBirthdayDate` (derived from originalBirthdayDateRef,
  // which the uncheck handler clears), this stays true until edit mode ends. It
  // keeps the birthday checkbox ENABLED after an uncheck so the multi-date rule
  // does not disable it and the user can re-check it.
  const [isEditingBirthdayRow, setIsEditingBirthdayRow] = useState<boolean>(false)
  useEffect(() => {
    if (!isInDateEditMode || !editDate) {
      setIsEditingBirthdayRow(false)
      return
    }
    // Capture once on entry (the ref still points at the birthday date here) and
    // retain it even if a later uncheck clears originalBirthdayDateRef.
    setIsEditingBirthdayRow((prev) => prev || datesMatch(editDate, originalBirthdayDateRef.current))
  }, [isInDateEditMode, editDate]);

  // Calculate per-day requested hours
  const activeHoursValues = isInDateEditMode ? currentEditingDateHours : hoursValues
  const perDayRequested = useMemo(() => {
    const vacation = Number.parseFloat(activeHoursValues.vacation || '0') || 0
    const float = Number.parseFloat(activeHoursValues.float || '0') || 0
    const flexible = Number.parseFloat(activeHoursValues.flexible || '0') || 0
    const other = Number.parseFloat(activeHoursValues.other || '0') || 0
    return vacation + float + flexible + other
  }, [activeHoursValues])

  // Auto-uncheck birthday when hours are entered
  useEffect(() => {
    if (perDayRequested > 0 && birthdayChecked) {
      setBirthdayChecked(false)
    }
  }, [perDayRequested, birthdayChecked])

  // Reset "Other" dropdown when flexible hours are entered
  useEffect(() => {
    const flexibleHours = Number.parseFloat(activeHoursValues.flexible || '0') || 0
    if (flexibleHours > 0 && leaveTypeSelected['other']) {
      setLeaveTypeSelected(prev => {
        const updated = { ...prev }
        delete updated['other']
        return updated
      })
    }
  }, [activeHoursValues.flexible])

  // Reset the "Other Types of Time Off" dropdown when its own hours are
  // cleared/removed. A leftover subtype selection with no hours behind it is
  // stale — it can no longer be correct once the user removes the hours that
  // selection applied to, and previously caused it to reappear (or block a
  // fresh selection) the next time hours were re-entered for "Other".
  useEffect(() => {
    const otherHours = Number.parseFloat(activeHoursValues.other || '0') || 0
    if (otherHours > 0) return
    if (isInDateEditMode && resolvedEditDate) {
      if (perDateOtherSubTypeStorage[resolvedEditDate]) {
        setPerDateOtherSubTypeStorage(prev => {
          const updated = { ...prev }
          delete updated[resolvedEditDate]
          return updated
        })
      }
    } else if (leaveTypeSelected['other']) {
      setLeaveTypeSelected(prev => {
        const updated = { ...prev }
        delete updated['other']
        return updated
      })
    }
  }, [activeHoursValues.other, isInDateEditMode, resolvedEditDate])

  // Clear validation errors when birthday is checked; reset ref when unchecked
  useEffect(() => {
    if (birthdayChecked) {
      setFieldErrors({})
      onValidationError?.(null)
    } else {
      // When birthday is unchecked, reset the validation ref so validateAllHours
      // will call onValidationError with new messages on next validation
      lastValidationMessagesRef.current = null
    }
  }, [birthdayChecked, onValidationError])

  // Req 1: Full Day radio is disabled when editing a date that already has another user's partial-day
  // request, forcing the user to create a Partial Day request to avoid a full-day conflict.
  // Mirrors performPartialDayOverlapCheck's datesToCheck below: outside an explicit edit-mode
  // session (e.g. DraftView opening a single date directly, not via editMode/editDate), fall back
  // to the lone active date in selectedDates so the same restriction still applies there.
  const isFullDayDisabled = useMemo(() => {
    // Whenever a specific date is actively being edited (editDate set while in
    // date-edit mode), the Duration radios only ever represent THAT date, so
    // its disabled state must be driven solely by whether the edit date itself
    // conflicts with an existing partial-day request — not by whether some
    // OTHER, unrelated date currently sitting in selectedDates conflicts.
    // Previously this only narrowed to editDate for a "genuine" edit (an
    // already-committed date, or the dedicated "add more dates" session),
    // which meant a freshly added, non-conflicting date kept Full Day disabled
    // just because an earlier, still-uncommitted date in the same selection
    // conflicted — blocking the very "switch to Full Day" action that Req 3's
    // deselect (handleDurationChange) relies on to drop that earlier date.
    if (isInDateEditMode && resolvedEditDate) {
      return !!otherPartialDayDates?.[resolvedEditDate];
    }
    // No specific date is actively being edited (e.g. a freshly built batch of
    // selected dates with no edit session open yet) — the shared radio applies
    // to the whole selection, so disable it if ANY selected date conflicts.
    return selectedDates.some((d) => !!otherPartialDayDates?.[d]);
  }, [isInDateEditMode, resolvedEditDate, selectedDates, otherPartialDayDates]);

  // Determine if birthday checkbox should be disabled
  // Birthday requires: exactly 1 date + Full Day duration + no hours entered + eligible + not already taken
  const isBirthdayCheckboxDisabled = useMemo(
    () => {
      const isNotFullDay = selectedDuration !== 'Full Day';
      // When editing the original birthday date (for the whole edit session, even
      // after an uncheck), the multi-date rule does not apply — that date is
      // still a valid single-birthday selection, so the checkbox stays enabled.
      const isMultipleDates = !isEditingBirthdayRow && selectedDates.length !== 1;
      const hasHoursEntered = perDayRequested > 0;
      return isNotFullDay || isMultipleDates || hasHoursEntered || isBirthdayTaken;
    },
    [selectedDuration, selectedDates.length, perDayRequested, isBirthdayTaken, isEditingBirthdayRow]
  );

  /**
   * Computes the total requested hours across all selected dates.
   * In date-edit mode only the edited date uses the live input; all others use stored values.
   * Extracted from the useMemo callback to reduce component-level cognitive complexity.
   */
 const computeTotalRequested = (): number => {
  if (isInDateEditMode && resolvedEditDate) {
    const isEditingNewDateInAddMoreFlow =
      isAddingMoreDates &&
      !(committedDates || []).some((date) => datesMatch(date, resolvedEditDate))

    if (isEditingNewDateInAddMoreFlow) {
      // During Add date to request, committed dates stay selected for context,
      // but the form total should only reflect uncommitted in-progress dates.
      // In this mode the current form entry applies to all currently adding dates.
      const uncommittedDates = sortedDates.filter(
        (date) => !(committedDates || []).some((committed) => datesMatch(committed, date))
      )

      return perDayRequested * uncommittedDates.length
    }

    const isBrandNewEditDate = !perDateHoursStorage[resolvedEditDate];

    // For a newly added date, show only this date's in-progress total.
    // This keeps the form header at 0.00 until user enters hours.
    if (isBrandNewEditDate) {
      return perDayRequested;
    }

    // Existing behavior for previously committed/editing existing dates.
    return sortedDates.reduce((sum, date) => {
      return datesMatch(date, resolvedEditDate) ? sum + perDayRequested : sum + calculatePerDateTotalHours(date);
    }, 0);
  }

  return sortedDates.reduce((sum, date) => {
    const dateHours = perDateHoursStorage[date] || hoursValues;
    return sum + Object.values(dateHours).reduce((s, h) => s + (Number.parseFloat(h) || 0), 0);
  }, 0);
};

  const totalRequested = useMemo(
    () => computeTotalRequested(),
    [perDayRequested, isInDateEditMode, resolvedEditDate, sortedDates, perDateHoursStorage, hoursValues]
  )

  // Note: Request type is now calculated per-date in timeEntries during submission

  const isRowHiddenByNotAvailable = (rowId: keyof HoursValues): boolean => {
    if (rowId === 'other') {
      return false
    }
    const rowBalance = resultingBalances.find((balance) => balance.id === rowId)
    // Hide the row when no balance entry exists (empty/missing results) OR
    // when the backend explicitly marks it as not available.
    return !rowBalance || rowBalance.notAvailable === true
  }

  const isFloatSentinelUnavailable = (rowId: keyof HoursValues): boolean => {
    if (rowId !== 'float') {
      return false
    }

    const availableValue = resultingBalances.find((balance) => balance.id === rowId)?.available
    return String(availableValue) === String(FLOAT_UNAVAILABLE_SENTINEL)
  }

  const leaveRows: {
  id: keyof HoursValues
  label: string
  showDropdown: boolean
}[] = [
      { id: 'vacation', label: 'Vacation (PTO/ETO)', showDropdown: false },
   { id: 'float', label: 'Float', showDropdown: false },
    { id: 'flexible', label: 'Flexible Personal Days', showDropdown: false },
    { id: 'other', label: 'Other Types of Time Off ', showDropdown: true },
]

  const visibleLeaveRows = leaveRows.filter((row) => !isRowHiddenByNotAvailable(row.id))
  /**
   * Handles selection of an "Other Types of Time Off" subtype from the dropdown.
   * In date-edit mode, writes only to that date's storage; otherwise updates the global selection.
   */
  const handleLeaveTypeSelect = (opt: { value: string }, rowId: string) => {
    if ((editMode || isInDateEditMode) && resolvedEditDate) {
      setPerDateOtherSubTypeStorage((prev) => ({ ...prev, [resolvedEditDate]: opt.value }))
    } else {
      setLeaveTypeSelected((p) => ({ ...p, [rowId]: opt.value }))
    }
    clearFieldError(rowId)
    if (rowId === 'other') clearFieldError('otherSubtype')
  }

const updateLeaveTypeHours = (
  id: keyof HoursValues,
  value: string
) => {
  const updatedHours = {
    ...hoursValues,
    [id]: value,
  }

  // Update ref immediately (before React render cycle)
  latestHoursValuesRef.current = updatedHours

  // Update state
  setHoursValues(updatedHours)

  if (isInDateEditMode) {
    const updatedEditingHours = {
      ...currentEditingDateHours,
      [id]: value,
    }

    latestHoursValuesRef.current = updatedEditingHours

    setCurrentEditingDateHours(updatedEditingHours)
  }

  clearFieldError(id)
}

  /**
   * Validates hour input using a linear scan to avoid regex backtracking issues.
   * Accepts digits with at most one decimal point (including empty string).
   */
  const isValidHoursInput = (value: string): boolean => {
    let decimalCount = 0

    for (const char of value) {
      if (char === '.') {
        decimalCount += 1
        if (decimalCount > 1) {
          return false
        }
        continue
      }

      if (char < '0' || char > '9') {
        return false
      }
    }

    return true
  }
  /**
   * Clears the inline validation error for the given field and the parent
   * validation banner immediately when the user focuses or corrects a field,
   * without waiting for the next Next/Submit click.
   */
  const clearFieldError = (fieldKey: string) => {
    setFieldErrors((prev) => {
      if (!prev[fieldKey]) return prev
      const updated = { ...prev }
      delete updated[fieldKey]
      return updated
    })
    // Also dismiss the parent banner so stale error messages don't persist
    // while the user is actively correcting their input.
    if (lastValidationMessagesRef.current !== null) {
      lastValidationMessagesRef.current = null
      onValidationError?.(null)
    }
  }

  /**
   * Calculates the sum of each leave-type's hours across all selected dates.
   * Used in edit mode to determine total balance impact before validating.
   */
  const computeTotalHoursByType = (): Record<string, number> => {
    const totals: Record<string, number> = { vacation: 0, float: 0, flexible: 0, other: 0 }
    for (const date of selectedDates) {
      const dateHours = getHoursForSpecificDate(date)
      for (const leaveType of Object.keys(dateHours)) {
        totals[leaveType] = (totals[leaveType] ?? 0) + (Number.parseFloat(dateHours[leaveType as keyof HoursValues] || '0') || 0)
      }
    }
    return totals
  }

  /**
   * Resolves the effective hours for a date while in edit mode, mirroring the
   * commit-time backfill in {@link handleNextOrUpdateClick}:
   * - the date currently being edited uses the live `currentEditingDateHours`;
   * - a date already committed to storage uses its stored hours;
   * - a date added but never individually saved inherits the active edit hours.
   *
   * @param date A selected date key (YYYY-MM-DD or MM/DD/YYYY).
   * @returns The hours that date will carry into the review/payload.
   */
  const getEffectiveEditModeHoursForDate = (date: string): HoursValues => {
    if (datesMatch(editDate, date)) {
      // In Add Date flow, user can type and immediately click Update. Prefer
      // the ref snapshot when state is still settling to avoid false no-hours.
      if (
        isAddingMoreDates &&
        !areHoursValuesEqual(latestHoursValuesRef.current, currentEditingDateHours)
      ) {
        return latestHoursValuesRef.current
      }
      return currentEditingDateHours
    }
    return (
      findByMatchingDate(perDateHoursStorage, date) || currentEditingDateHours
    )
  }

  /**
   * Returns the selected non-birthday dates that would reach the review step
   * with zero hours — i.e. dates the user added during an edit session but never
   * gave hours to. Used to enforce that every date has valid hours before the
   * user can proceed (HRCOM-104959). Only applies while editing; returns an
   * empty list outside edit mode so the standard first-screen flow is untouched.
   *
   * @returns The list of dates missing hours (empty when none are missing).
   */
  const findDatesWithoutHours = (): string[] => {
    if (!isInDateEditMode || !editDate) return []
    const birthdayDate = originalBirthdayDateRef.current
    return selectedDates.filter((date) => {
      // The birthday date is a fixed full-day type and intentionally has no hours.
      if (date === birthdayDate) return false
      return sumHoursValues(getEffectiveEditModeHoursForDate(date)) === 0
    })
  }

  /**
   * Emits the "no hours entered" validation banner and inline error for dates
   * that have no hours in edit mode. Releases vacation-balance banner ownership
   * so the balance effect cannot clear this banner on the next render.
   *
   * @returns A single-element message array with the appropriate "no hours" error.
   */
  const emitMissingHoursError = (): string[] => {
    const noHoursMessage =
      selectedDuration === 'Partial Day'
        ? ValidationMessages.NO_HOURS_ENTERED_PARTIAL_DAY
        : ValidationMessages.NO_HOURS_ENTERED
    // Emit the banner UNCONDITIONALLY. Gating this behind the dedup ref previously
    // let the banner be skipped when the parent cleared its state elsewhere while the
    // ref still held this message — leaving the inline error visible but no banner.
    // validateAllHours only runs from explicit Next/Update/Submit clicks so
    // unconditional emission cannot cause a render loop (HRCOM-104959).
    //
    // Release vacation-balance banner ownership: with an empty active date its hours
    // are 0, so the balance effect would otherwise fire its clear branch on the next
    // render and wipe this no-hours banner (HRCOM-104959).
    setFieldErrors({ vacation: 'Required' })
    vacationBalanceBannerActiveRef.current = false
    lastValidationMessagesRef.current = noHoursMessage
    onValidationError?.([noHoursMessage])
    return [noHoursMessage]
  }

  /**
   * Builds the validation inputs — hours to validate, effective leave-type
   * selection, and adjusted available balances — for one validation pass.
   *
   * In date-edit mode the currently edited date's live hours drive the values;
   * outside edit mode the global form hours are used.
   *
   * @param isEditModeActive - `true` when both `isInDateEditMode` and `editDate` are set.
   */
  const buildValidationContext = (
  isEditModeActive: boolean,
  overrideHours?: HoursValues
) => {
    const totalHoursByType = isEditModeActive
      ? computeTotalHoursByType()
      : { vacation: 0, float: 0, flexible: 0, other: 0 }
    const adjustedAvailableBalances = buildAdjustedBalances(resultingBalances, isEditModeActive, totalHoursByType)
    // Duration limits (min 0.25 hrs, max 16 hrs) are PER DAY constraints — validated
    // against per-day hours, not the running total across multiple dates.
const hoursToValidate =
  overrideHours ||
  (isEditModeActive ? currentEditingDateHours : hoursValues)
    // In date-edit mode the "Other Types" dropdown writes to perDateOtherSubTypeStorage[editDate]
    // instead of the global leaveTypeSelected, so validateEntries must receive the per-date
    // selection to avoid false "Other sub type is required" errors.
    const effectiveLeaveTypeSelected =
      isEditModeActive && resolvedEditDate && perDateOtherSubTypeStorage[resolvedEditDate]
        ? { ...leaveTypeSelected, other: perDateOtherSubTypeStorage[resolvedEditDate] }
        : leaveTypeSelected
    return { hoursToValidate, effectiveLeaveTypeSelected, adjustedAvailableBalances }
  }
      const getEffectiveDuration = () => {
  if (isInDateEditMode && resolvedEditDate && perDateDurationStorage[resolvedEditDate]) {
    return perDateDurationStorage[resolvedEditDate]
  }

  return selectedDuration
}


  // Issue (HRCOM-104959): in edit mode, block navigation until EVERY selected
  // non-birthday date has hours. A date added during editing that the user
  // never gave hours to must surface the mandatory "no hours" error instead of
  // silently proceeding to the review step.
  // Extracted from validateAllHours purely to keep its Cognitive Complexity
  // under Sonar's threshold — same guard, same return value, just relocated.
  const checkMissingHoursGuard = (): string[] | null => {
    const datesMissingHours = findDatesWithoutHours()
    if (datesMissingHours.length === 0) return null

    // Add Date flow guard: if the user has already entered any hours on the
    // active form, do not short-circuit to NO_HOURS. Let full validation run
    // so balance/duration messages (e.g. FLOAT_BALANCE) are shown instead.
    const activeHoursSnapshot =
      isAddingMoreDates && isInDateEditMode
        ? latestHoursValuesRef.current
        : currentEditingDateHours
    if (sumHoursValues(activeHoursSnapshot) > 0) {
      // Continue to field-level validation below.
      return null
    }
    return emitMissingHoursError()
  }

  // In "Add date to request" flow, prefer the latest typed hours from the
  // ref over state regardless of edit-state. React state updates are async;
  // this prevents stale zero-hour validation when users type and immediately
  // click Next/Update in EditSubmitted add mode.
  // Extracted from validateAllHours purely to keep its Cognitive Complexity
  // under Sonar's threshold — same computation, just relocated.
  const computeValidationHours = (isEditModeActive: boolean): HoursValues => {
    const shouldPreferLatestInput = isAddingMoreDates
    return shouldPreferLatestInput
      ? {
          ...hoursValues,
          ...currentEditingDateHours,
          ...latestHoursValuesRef.current,
        }
      : {
          ...hoursValues,
          ...(isEditModeActive ? currentEditingDateHours : {}),
        }
  }

  // Vacation hours can be individually valid (e.g. 5 of a 0.25-16 range) yet
  // still exceed the selected partial-day time window (e.g. a 2-hour window)
  // — that's a distinct violation from vacationDurationInvalid and must also
  // keep the inline vacation marker instead of being suppressed as a balance error.
  // Extracted from validateAllHours purely to keep its Cognitive Complexity
  // under Sonar's threshold — same computation, just relocated.
  const computePartialDayHoursDurationMismatch = (messages: string[] | null | undefined): boolean => {
    const messageList = messages || []
    return (
      messageList.includes(ValidationMessages.PARTIAL_DAY_HOURS_EXCEED_DURATION) ||
      messageList.includes(ValidationMessages.PARTIAL_DAY_HOURS_LESS_THAN_DURATION)
    )
  }

  // Keep banner + inline in sync on explicit Next/Update clicks: if inline
  // errors are present but filtering removed every banner message, restore a
  // minimal fallback message so the user is never left with inline "Revise"
  // and no banner context.
  // Extracted from validateAllHours purely to keep its Cognitive Complexity
  // under Sonar's threshold — same fallback logic, just relocated.
  const buildFallbackDisplayMessages = (
    nextFieldErrors: Record<string, string>,
    hoursToValidate: HoursValues,
    allMessages: string[]
  ): string[] => {
    // Prefer field-specific context for explicit Next/Update validation.
    // Only fall back to BALANCE when no field-specific banner can be derived.
    const fallbackMessages: string[] = []
    if (nextFieldErrors.vacation) fallbackMessages.push(ValidationMessages.DURATION)
    if (nextFieldErrors.float) fallbackMessages.push(ValidationMessages.FLOAT_DURATION)
    if (nextFieldErrors.flexible) {
      const flexibleHours = Number.parseFloat(hoursToValidate.flexible || '0') || 0
      fallbackMessages.push(
        flexibleHours > 0 && flexibleHours < 2
          ? ValidationMessages.FLEXIBLE_MIN_TWO_HOURS
          : ValidationMessages.FLEXIBLE_DURATION
      )
    }
    if (nextFieldErrors.other) fallbackMessages.push(ValidationMessages.OTHER_DURATION)
    const fallback = Array.from(new Set(fallbackMessages))

    if (fallback.length === 0 && allMessages.includes(ValidationMessages.BALANCE)) {
      return [ValidationMessages.BALANCE]
    }
    return fallback
  }

  // Emit the banner UNCONDITIONALLY on every explicit Next/Update click.
  // A previous dedup guard here suppressed re-emitting an identical result after
  // the parent had cleared its banner on a date switch, leaving the inline error
  // visible with no banner. validateAllHours runs only from click handlers so
  // unconditional emission cannot cause a render loop (HRCOM-104959).
  // Extracted from validateAllHours purely to keep its Cognitive Complexity
  // under Sonar's threshold — same emission logic, just relocated.
  const emitValidationResult = (displayMessages: string[]): string[] | null => {
    const messagesKey =
      displayMessages.length > 0 ? displayMessages.join('|') : null
    lastValidationMessagesRef.current = messagesKey
    // When emitting a blocking banner, release the balance-banner ownership flag so
    // the balance effect's clear branch cannot wipe this banner on the next render
    // (HRCOM-104959).
    if (messagesKey) {
      vacationBalanceBannerActiveRef.current = false
    }
    onValidationError?.(displayMessages.length > 0 ? displayMessages : null)

    return displayMessages.length > 0 ? displayMessages : null
  }

  const validateAllHours = (): string[] | null => {
    // Birthday requests don't require hours validation — skip all validation.
    if (birthdayChecked) {
      setFieldErrors({})
      return null
    }

    // No date is currently active in the form — this happens in DraftView's
    // hideReviewTable mode when the user clicks Save as Draft/Submit Request
    // straight from landing on the draft, or mid "Add More Dates" before a
    // new date is picked. There's nothing new to validate: the submitted
    // payload is built entirely from already-saved dates via
    // `preservedDatesPayload`. Without this guard,
    // `allNonBirthdayDatesHaveStoredHours` treats an empty `selectedDates` as
    // "nothing has stored hours" (its `nonBirthday.length > 0` check fails
    // vacuously), so the code below fell through to validating the form's
    // default/empty `hoursValues` and threw "you need to enter hours" even
    // though no date was being edited.
    if (selectedDates.length === 0) {
      setFieldErrors({})
      return null
    }

    const missingHoursResult = checkMissingHoursGuard()
    if (missingHoursResult !== null) return missingHoursResult

    // If all non-birthday dates already have hours committed to storage, skip
    // form-level validation — the review table will use the stored hours directly.
    // EXCEPTION: never skip when in date-edit mode. The user may have typed new
    // values (e.g. 17 hours) that differ from what is in storage; those live
    // inputs in currentEditingDateHours MUST go through validateEntries before
    // they are committed. Without this guard the stored-hours shortcut fires
    // (all dates pass the hasStoredHours check) and the 17 is accepted silently.
    const hasStoredHours = allNonBirthdayDatesHaveStoredHours(
      selectedDates,
      originalBirthdayDateRef.current,
      perDateHoursStorage
    )
    // Single flag to avoid repeating isInDateEditMode && editDate throughout.
    const isEditModeActive = !!(isInDateEditMode && editDate)
    if (hasStoredHours && !isEditModeActive) {
      setFieldErrors({})
      return null
    }

    const validationHours = computeValidationHours(isEditModeActive)

    const {
      hoursToValidate,
      effectiveLeaveTypeSelected,
      adjustedAvailableBalances,
    } = buildValidationContext(isEditModeActive, validationHours)

    const { nextFieldErrors, messages } = validateEntries(
      Object.fromEntries(Object.entries(hoursToValidate)) as Record<
        string,
        string
      >,
      adjustedAvailableBalances,
      effectiveLeaveTypeSelected,
      selectedDuration,
      undefined,
      undefined,
      selectedDuration === 'Partial Day' ? selectedTime : undefined
    )

    // When multiple dates are selected (not in edit mode), validate that total hours
    // across all dates don't exceed the available balance for float and flexible only.
    const multiDateBalanceErrors =
      !isEditModeActive && selectedDates.length > 1
        ? buildMultiDateBalanceErrors(
            hoursToValidate,
            selectedDates.length,
            resultingBalances,
            nextFieldErrors
          )
        : []

    const partialDayHourErrors = validatePartialDayTotalHours()

    const allMessages = Array.from(
      new Set([
        ...(messages || []),
        ...multiDateBalanceErrors,
        ...partialDayHourErrors,
      ])
    )
    const { vacationDurationInvalid } = getValidationConfig(hoursToValidate)
    const partialDayHoursDurationMismatch = computePartialDayHoursDurationMismatch(messages)

    // Keep inline vacation error only when vacation itself is invalid (duration),
    // exceeds the partial-day time window, or required; suppress balance/indirect
    // vacation errors so float-only errors don't leak into vacation field.
    suppressNonDurationVacationError(
      nextFieldErrors,
      vacationDurationInvalid || partialDayHoursDurationMismatch
    )
    setFieldErrors(nextFieldErrors)

    // Filter: remove EDUCATION_COMMENT_REQUIRED (submit-only), spurious duration
    // messages, and BALANCE (rendered live by the vacation-balance effect).
    let displayMessages = filterDisplayMessages(allMessages, hoursToValidate)

    // Keep banner + inline in sync on explicit Next/Update clicks: if inline
    // errors are present but filtering removed every banner message, restore a
    // minimal fallback message so the user is never left with inline "Revise"
    // and no banner context.
    if (
      displayMessages.length === 0 &&
      Object.keys(nextFieldErrors).length > 0
    ) {
      displayMessages = buildFallbackDisplayMessages(nextFieldErrors, hoursToValidate, allMessages)
    }

    return emitValidationResult(displayMessages)
  }

  /**
   * Runs the partial-day overlap check for the user's selected time against any
   * other-user partial-day requests on the same date(s).
   * Calls `onValidationError` and scrolls to the alert banner when an overlap is found.
   *
   * @returns `true` when an overlap was detected and the caller should abort (return early).
   */
  const performPartialDayOverlapCheck = (): boolean => {
    if (selectedDuration !== 'Partial Day' || !otherPartialDayDates) return false;
    // Mirrors isFullDayDisabled's isGenuineDateEdit: only narrow to the single
    // editDate for a genuine edit of an already-committed date, or the
    // dedicated "add more dates" session. A forced edit session for a fresh,
    // not-yet-committed date must validate every currently selected date,
    // since they all share this one Partial Day entry.
    const isGenuineDateEdit =
      isInDateEditMode &&
      !!resolvedEditDate &&
      ((committedDates || []).includes(resolvedEditDate) || isAddingMoreDates);
    const datesToCheck =
      isGenuineDateEdit && resolvedEditDate ? [resolvedEditDate] : selectedDates;
    // Compute the user's window once — shared across all range comparisons.
    const userStart = timeFieldsToMinutes(selectedTime.from);
    const userEnd = timeFieldsToMinutes(selectedTime.to);
    if (userStart === null || userEnd === null) return false;
    const userRange: [number, number] = [userStart, userEnd];
    for (const date of datesToCheck) {
      // Check against EVERY existing partial-day range for this date so a second
      // or third request's window is never silently skipped.
      const otherRanges = otherPartialDayDates[date];
      if (otherRanges && hasOverlappingPartialRange(userRange, otherRanges)) {
        onValidationError?.([ValidationMessages.PARTIAL_DAY_OVERLAP]);
        setShowHistoryTable(false);
        scrollToTopAndFocusAlert();
        return true;
      }
    }
    return false;
  };

  const validatePartialDayTotalHours = (): string[] => {
  if (getEffectiveDuration() !== 'Partial Day') {
    return []
  }

  const errors: string[] = []

  selectedDates.forEach(date => {

    const existingHours =
      perDateHoursStorage[date]
        ? Object.values(perDateHoursStorage[date])
            .reduce(
              (sum, h) => sum + (Number.parseFloat(h) || 0),
              0
            )
        : 0


    const currentHours =
      Object.values(currentEditingDateHours)
        .reduce(
          (sum, h) => sum + (Number.parseFloat(h) || 0),
          0
        )


    // avoid counting same request while editing
    const totalHours =
      isInDateEditMode
        ? currentHours
        : existingHours + currentHours


    if (totalHours > 16) {
      errors.push(
        "A partial day request cannot exceed 16 hours for the same date."
      )
    }
  })


  return errors
}
  /**
   * Handle Next/Update button click
   * - Validates all hours
   * - Saves edited date hours, duration, and time to storage
   * - Shows review table
   */
  // Save the actively edited date's hours, then backfill any other selected dates
  // that were added to the picker but never saved (e.g. user picked 07/11, skipped
  // to 07/15 without clicking Next for 07/11 — both should receive the entered hours).
  // Keyed by resolvedEditDate (not the raw editDate prop, which can arrive as
  // MM/DD/YYYY from the review table's edit icon) so this commits IN PLACE to the
  // date's existing storage entry instead of forking a second, disconnected one.
  // Extracted from handleNextOrUpdateClick purely to keep its Cognitive Complexity
  // under Sonar's threshold — same logic, same call order, just relocated.
  // `resolvedEditDate` is passed in (rather than closed over) so TypeScript's
  // non-null narrowing from the caller's `if (isInDateEditMode && resolvedEditDate)`
  // carries through to this function's body.
  const commitEditModeDateEntries = (resolvedEditDate: string): void => {
      const newStorage: Record<string, HoursValues> = {
        ...perDateHoursStorage,
        [resolvedEditDate]: currentEditingDateHours,
      }
      selectedDates.forEach((date) => {
        // Skip birthday date - keep it as 0 hours (only when this is an actual birthday request)
        if (
          selectedDates.length > 1 &&
          isBirthdayRequestRef.current &&
          date === originalBirthdayDateRef.current
        ) {
          // Birthday date: do nothing, keep original hours
        } else if (!newStorage[date]) {
          newStorage[date] = currentEditingDateHours
        }
      })
      setPerDateHoursStorage(newStorage)

      setPerDateDurationStorage((prev) => {
        const updated = { ...prev, [resolvedEditDate]: selectedDuration }
        selectedDates.forEach((date) => {
          if (!updated[date]) updated[date] = selectedDuration
        })
        return updated
      })
      setPerDateTimeStorage((prev) => {
        const updated = { ...prev, [resolvedEditDate]: selectedTime }
        selectedDates.forEach((date) => {
          if (!updated[date]) updated[date] = selectedTime
        })
        return updated
      })

      // If Other hours were cleared, remove the stored subtype so the dropdown
      // resets to empty on the next edit. If hours are still > 0, the subtype
      // was already written by handleLeaveTypeSelect on dropdown change.
      // Also backfill the same subtype to other selected dates that share the
      // editDate's Other hours but never got their own dropdown selection saved
      // (mirrors the hours/duration/time backfill above).
      // Computed as a local snapshot (not inside the setState updater) so the
      // synchronous onReviewReady(reviewRows) call below — the only path
      // DraftView uses, since it renders with hideReviewTable — reflects the
      // backfilled values instead of the pre-update state closure.
      const newPerDateOtherSubTypes: Record<string, string> = {
        ...perDateOtherSubTypeStorage,
      }
      const editDateOtherHours =
        Number.parseFloat(currentEditingDateHours.other || '0') || 0
      if (editDateOtherHours === 0) {
        delete newPerDateOtherSubTypes[resolvedEditDate]
      }
      const editDateSubType = newPerDateOtherSubTypes[resolvedEditDate]
      selectedDates.forEach((date) => {
        if (datesMatch(date, resolvedEditDate)) return
        const dateOtherHours =
          Number.parseFloat(newStorage[date]?.other || '0') || 0
        if (dateOtherHours === 0) {
          delete newPerDateOtherSubTypes[date]
        } else if (!newPerDateOtherSubTypes[date] && editDateSubType) {
          newPerDateOtherSubTypes[date] = editDateSubType
        }
      })
      setPerDateOtherSubTypeStorage(newPerDateOtherSubTypes)
      if (onReviewReady) {
        const effectiveDurations: Record<string, string> = {
          ...perDateDurationStorage,
          [resolvedEditDate]: selectedDuration,
        }
        selectedDates.forEach((date) => {
          if (!effectiveDurations[date])
            effectiveDurations[date] = selectedDuration
        })
        const effectiveTimes: Record<
          string,
          { from: TimeFields; to: TimeFields }
        > = { ...perDateTimeStorage, [resolvedEditDate]: selectedTime }
        selectedDates.forEach((date) => {
          if (!effectiveTimes[date]) effectiveTimes[date] = selectedTime
        })
        const reviewRows = selectedDates.map((date) => {
          const dateHours = newStorage[date] || currentEditingDateHours
          const total = birthdayChecked
            ? 8
            : Object.values(dateHours).reduce(
                (sum, h) => sum + (Number.parseFloat(h) || 0),
                0
              )
          const duration = effectiveDurations[date] || selectedDuration
          const time = effectiveTimes[date] || selectedTime
          const times =
            duration === 'Partial Day'
              ? formatTime(time.from).toLowerCase().replace(' ', '') +
                ' - ' +
                formatTime(time.to).toLowerCase().replace(' ', '')
              : 'Full Day'
          // Use the freshly-snapshotted per-date Other subtype so each row
          // reflects the just-backfilled value, not the pre-update state closure
          const effectiveLeaveType = newPerDateOtherSubTypes[date]
            ? { ...leaveTypeSelected, other: newPerDateOtherSubTypes[date] }
            : leaveTypeSelected
          // Check if this is the original birthday date BEFORE using birthdayChecked
          const isOriginalBirthdayDate =
            date === originalBirthdayDateRef.current &&
            originalBirthdayRequestTypeRef.current
          const reqType = isOriginalBirthdayDate
            ? { displayType: originalBirthdayRequestTypeRef.current }
            : determineRequestType(
                dateHours,
                effectiveLeaveType,
                birthdayChecked
              )
          return {
            id: `row-${date}`,
            requestType: reqType?.displayType || '',
            date: formatDateForDisplay(date),
            // Full weekday name to match Figma (e.g. "Monday" not "Mon")
            day: new Date(date).toLocaleDateString('en-US', {
              weekday: 'long',
            }),
            times,
            requestedHours:
              birthdayChecked || isOriginalBirthdayDate
                ? 'NA'
                : total.toFixed(2),
            reviewHoursValues: { ...dateHours },
            reviewDuration: duration as 'Full Day' | 'Partial Day',
            reviewTime: duration === 'Partial Day' ? { ...time } : undefined,
            reviewOtherSubType: effectiveLeaveType.other,
            hoursInOtherRequests: formatOtherRequestBreakdown(
              getOtherRequestBreakdownForDate(date, otherRequestHoursByDate)
            ),
          }
        })
        onReviewReady(reviewRows)
      }
  }

  // Extracted from handleNextOrUpdateClick purely to keep its Cognitive Complexity
  // under Sonar's threshold — same logic, same call order, just relocated.
  const commitNormalModeDateEntries = (): void => {
      const newStorage: Record<string, HoursValues> = { ...perDateHoursStorage }
      const effectiveDurations: Record<string, string> = {
        ...perDateDurationStorage,
      }
      const effectiveTimes: Record<
        string,
        { from: TimeFields; to: TimeFields }
      > = { ...perDateTimeStorage }

      selectedDates.forEach((date) => {
        // In non-edit mode: skip birthday date, keep it as birthday with 0 hours.
        // Guard with isBirthdayRequestRef so the first-selected date of a normal request
        // is never accidentally treated as a birthday and excluded from per-date storage.
        if (
          selectedDates.length > 1 &&
          isBirthdayRequestRef.current &&
          date === originalBirthdayDateRef.current
        ) {
          // Birthday date: do nothing, keep as 0 hours
        } else if (!newStorage[date]) {
          newStorage[date] = hoursValues
        }
        if (!effectiveDurations[date])
          effectiveDurations[date] = selectedDuration
        if (!effectiveTimes[date]) effectiveTimes[date] = selectedTime
      })

      setPerDateHoursStorage(newStorage)

      setPerDateDurationStorage((prev) => {
        const newDurations = { ...prev }
        selectedDates.forEach((date) => {
          if (!newDurations[date]) {
            newDurations[date] = selectedDuration
          }
        })
        return newDurations
      })

      setPerDateTimeStorage((prev) => {
        const newTimes = { ...prev }
        selectedDates.forEach((date) => {
          if (!newTimes[date]) {
            newTimes[date] = selectedTime
          }
        })
        return newTimes
      })

      // Snapshot the effective Other subtype per date. Only store when Other hours
      // were actually entered — never carry a stale dropdown selection for a date
      // where Other hours are zero. Mirrors the fix in the isInDateEditMode Update path.
      const newPerDateOtherSubTypes = { ...perDateOtherSubTypeStorage }
      selectedDates.forEach((date) => {
        const otherHoursForDate =
          Number.parseFloat((newStorage[date] || hoursValues).other || '0') || 0
        if (otherHoursForDate === 0) {
          // Other hours are zero — clear any previously stored subtype so the
          // dropdown resets to empty on the next edit instead of showing a
          // stale selection from a prior entry (e.g. user had Other hours then
          // removed them, or selected a subtype without entering hours).
          delete newPerDateOtherSubTypes[date]
        } else if (
          leaveTypeSelected['other'] &&
          !newPerDateOtherSubTypes[date]
        ) {
          // Other hours present and no subtype stored yet — snapshot the global
          // selection so subsequent single-date edits only affect this date.
          newPerDateOtherSubTypes[date] = leaveTypeSelected['other']
        }
      })
      setPerDateOtherSubTypeStorage(newPerDateOtherSubTypes)

      // Notify parent with updated rows for all selected dates
      if (onReviewReady) {
        const reviewRows = selectedDates.map((date) => {
          const dateHours = newStorage[date] || hoursValues
          const total = birthdayChecked
            ? 8
            : Object.values(dateHours).reduce(
                (sum, h) => sum + (Number.parseFloat(h) || 0),
                0
              )
          const duration = effectiveDurations[date] || selectedDuration
          const time = effectiveTimes[date] || selectedTime
          const times =
            duration === 'Partial Day'
              ? formatTime(time.from).toLowerCase().replace(' ', '') +
                ' - ' +
                formatTime(time.to).toLowerCase().replace(' ', '')
              : 'Full Day'
          // Use the freshly-snapshotted per-date Other subtype for accurate request type
          const effectiveLeaveType = newPerDateOtherSubTypes[date]
            ? { ...leaveTypeSelected, other: newPerDateOtherSubTypes[date] }
            : leaveTypeSelected
          // Check if this is the original birthday date BEFORE using birthdayChecked
          const isOriginalBirthdayDate =
            date === originalBirthdayDateRef.current &&
            originalBirthdayRequestTypeRef.current
          const reqType = isOriginalBirthdayDate
            ? { displayType: originalBirthdayRequestTypeRef.current }
            : determineRequestType(
                dateHours,
                effectiveLeaveType,
                birthdayChecked
              )
          return {
            id: `row-${date}`,
            requestType: reqType?.displayType || '',
            date: formatDateForDisplay(date),
            // Full weekday name to match Figma (e.g. "Monday" not "Mon")
            day: new Date(date).toLocaleDateString('en-US', {
              weekday: 'long',
            }),
            times,
            requestedHours:
              birthdayChecked || isOriginalBirthdayDate
                ? 'NA'
                : total.toFixed(2),

            reviewHoursValues: { ...dateHours },
            reviewDuration: duration as 'Full Day' | 'Partial Day',
            reviewTime: duration === 'Partial Day' ? { ...time } : undefined,
            reviewOtherSubType: effectiveLeaveType.other,
            hoursInOtherRequests: formatOtherRequestBreakdown(
              getOtherRequestBreakdownForDate(date, otherRequestHoursByDate)
            ),
          }
        })
        onReviewReady(reviewRows)
      }
  }

  const handleNextOrUpdateClick = () => {
    // The 8-day gap rule only applies to Vacation Bid requests — gating this
    // on isResubmitFlow alone fired it for every normal resubmit too, since
    // isResubmitFlow is true for both request types.
    if (isResubmitFlow && isVacationBidRequest) {
      const dateGapError = validateDateGaps(selectedDates)

      if (dateGapError) {
        onValidationError?.([dateGapError])
        scrollToTopAndFocusAlert()
        return
      }
    }
    setIsNextClicked(true)
    const validationMessages = validateAllHours()
    if (validationMessages) {
      setCurrentEditingDateHours((prev) => ({
        ...prev,
        ...hoursValues,
      }))

      setShowHistoryTable(false)
      scrollToTopAndFocusAlert()
      return
    }

    // Req 2: Partial-day overlap check — compare the user's selected partial times against
    // any existing other-user partial-day requests for the same date(s).
    if (performPartialDayOverlapCheck()) return

    // Check for date gap exceeding 8 days before entering review — this
    // handler drives Next/Update for every AddTimeToRequest consumer
    // (create, draft, and edit/resubmit), so fixing it here applies
    // uniformly instead of only at final Save/Submit like before.
    // Must include committedDates (e.g. a draft's already-saved dates) —
    // selectedDates alone only holds this session's picks, so adding a
    // single new date more than 8 days past the draft's last saved date
    // never had 2+ dates to compare and silently passed validation.
    const allDatesForGapCheck = Array.from(
      new Set([...(committedDates || []), ...selectedDates])
    )
    const dateGapError = validateDateGaps(allDatesForGapCheck)
    if (dateGapError) {
      onValidationError?.([dateGapError])
      scrollToTopAndFocusAlert()
      return
    }

  // Switch to review immediately after validation succeeds so the form section
  // does not remain visible if parent row updates re-render first.
  setShowHistoryTable(true)

    if (isInDateEditMode && resolvedEditDate) {
      commitEditModeDateEntries(resolvedEditDate)
    } else {
      commitNormalModeDateEntries()
    }
    setIsInDateEditMode(false)
    setCommentError('')
    onValidationError?.(null)
    onExitEditMode?.()
  }

  const   handleTimeSelect = (side: 'from' | 'to', field: keyof TimeFields, opt: DropdownOption) => {
    setSelectedTime((prev) => {
      const next = { ...prev, [side]: { ...prev[side], [field]: opt } };
      
      // Auto-populate missing fields with defaults
      next[side].minute ??= minutesOptions[0]; // Default to "00"
      next[side].hour ??= hoursOptions[0]; // Default to "1"
      next[side].ampm ??= ampmOptions[0]; // Default to "AM"
      

      return next;
    });
  };

  /**
   * Remove a date from the request (from Review Table)
   * - Notifies parent to update selectedDates
   * - Cleans up per-date hours, duration, and time storage for that date
   */
  const onRemoveDateFromReviewTable = (dateToRemove: string) => {
    // Tell parent to remove this date from selectedDates
    onRemoveDateClick?.(dateToRemove)
    
    // Remove stored hours, duration, and time for this date
    setPerDateHoursStorage((prev) => {
      const updated = { ...prev }
      delete updated[dateToRemove]
      return updated
    })
    setPerDateDurationStorage((prev) => {
      const updated = { ...prev }
      delete updated[dateToRemove]
      return updated
    })
    setPerDateTimeStorage((prev) => {
      const updated = { ...prev }
      delete updated[dateToRemove]
      return updated
    })
    setPerDateOtherSubTypeStorage((prev) => {
      const updated = { ...prev }
      delete updated[dateToRemove]
      return updated
    })
  }

  const isEducationOtherRequest = () => {
    // Check if ANY date in the review table has education time
    // First check per-date storage for explicit education selections
    if (Object.values(perDateOtherSubTypeStorage).includes('OTH_ET')) {
      return true
    }
    
    // Then check if global selection is education and hasn't been overridden per-date
    if (leaveTypeSelected['other'] === 'OTH_ET') {
      // Check if there are any dates that don't have an explicit per-date override
      const hasAnyDateWithoutOverride = selectedDates.some(date => !perDateOtherSubTypeStorage?.[date])
      if (hasAnyDateWithoutOverride) {
        return true
      }
    }
    
    return false
  }

  // Memoize education check to use in effect dependencies
  const isEducationRequest = useMemo(
    () => isEducationOtherRequest(),
    [perDateOtherSubTypeStorage, leaveTypeSelected, selectedDates]
  )

  // Clear comment error when education type is deselected or in non-education edit mode
  useEffect(() => {
    if (!isEducationRequest && !isResubmitFlow) {
      setCommentError('')
    }
  }, [isEducationRequest, isResubmitFlow])


  /**
   * Resolves the exact set of (original-format, e.g. MM/DD/YYYY) date keys that
   * will end up in the submission payload / review table for a given save mode.
   * Shared by `buildSubmissionPayload` and the pre-submit date-gap check so both
   * always agree on which dates are actually being saved or submitted.
   *
   * `restrictToCommitted`: when true (Save as Draft), only dates already
   * committed to the review table are included — any date currently being
   * added or edited but not yet confirmed via Next/Update is dropped, so an
   * in-progress, unconfirmed edit is never persisted to the draft.
   */
  const getPayloadDateKeys = (restrictToCommitted: boolean): string[] => {
    const allDateKeys = Array.from(
      new Set([
        ...selectedDates,
        ...Object.keys(perDateHoursStorage),
        ...Object.keys(perDateDurationStorage),
        ...Object.keys(perDateTimeStorage),
        ...Object.keys(perDateOtherSubTypeStorage),
      ])
    )
    // Only apply the restriction when `committedDates` is actually provided —
    // it signals a review-table session where "not yet committed" is a
    // meaningful distinction. When the prop is absent entirely (e.g. a
    // DraftView single-date edit merged via `preservedDatesPayload`), every
    // date being edited belongs in the draft.
    return restrictToCommitted && committedDates
      ? allDateKeys.filter((d) => committedDates.includes(d))
      : allDateKeys
  }

  /**
   * Build payload from component state
   * Captures all form values and formats them for API submission
   * Handles birthday as special case: no hours (null) and single full day only
   */
  const buildSubmissionPayload = (restrictToCommitted: boolean = false) => {
    const payloadDateKeys = getPayloadDateKeys(restrictToCommitted)

    const formattedSelectedDates = payloadDateKeys.map(normalizeDateKey);

    const startTime = selectedDuration === 'Partial Day' 
      ? { hour: selectedTime.from.hour?.value || '1', minute: selectedTime.from.minute?.value || '00', ampm: selectedTime.from.ampm?.value || 'AM' }
      : undefined;

    const endTime = selectedDuration === 'Partial Day'
      ? { hour: selectedTime.to.hour?.value || '1', minute: selectedTime.to.minute?.value || '00', ampm: selectedTime.to.ampm?.value || 'AM' }
      : undefined;

    const perDateHours: Record<string, HoursValues> = {};

    payloadDateKeys.forEach((originalDateStr, index) => {
      const apiDateStr = formattedSelectedDates[index];

      // If this is the original birthday date and it was marked as birthday, set 0 hours
      if (isBirthdayRequestRef.current && originalDateStr === originalBirthdayDateRef.current) {
        perDateHours[apiDateStr] = { vacation: '0', float: '0', flexible: '0', other: '0' };
      } else {
        const storedHours = perDateHoursStorage[originalDateStr];
        // Save as Draft (restrictToCommitted): a committed date can be open in
        // the form with NO perDateHoursStorage entry yet — e.g. DraftView's
        // per-date "pencil edit" flow hydrates the visible fields straight from
        // initialHoursByDate rather than perDateHoursStorage/Next-Update. In
        // that case the live `hoursValues` is just an in-progress, unconfirmed
        // edit — fall back to the date's ORIGINAL saved value instead, never
        // the live form state, so an edit the user never confirmed can't leak
        // into the draft. Only Next/Update (restrictToCommitted=false, or a
        // perDateHoursStorage entry already present) should ever save the live value.
        const originalHours = restrictToCommitted
          ? findByMatchingDate(initialHoursByDate, originalDateStr)
          : undefined;
        perDateHours[apiDateStr] = storedHours || originalHours || hoursValues;
      }
    });

    const perDateDurations: Record<string, 'Full Day' | 'Partial Day'> = {};
    payloadDateKeys.forEach((originalDateStr, index) => {
      const apiDateStr = formattedSelectedDates[index];
      const storedDuration = perDateDurationStorage[originalDateStr];
      // See the perDateHours fallback above — same rationale applies to duration.
      const originalDuration = restrictToCommitted
        ? findByMatchingDate(initialDurationByDate, originalDateStr)
        : undefined;
      perDateDurations[apiDateStr] =
        (storedDuration as 'Full Day' | 'Partial Day') || originalDuration || selectedDuration;
    });

    /** Converts either the DropdownOption-shaped `TimeFields` window (perDateTimeStorage/selectedTime) or the
     * raw string-shaped window (`initialTimeByDate`) into the plain string shape the payload expects. */
    const toPayloadTime = (
      time:
        | { from: TimeFields; to: TimeFields }
        | { from: { hour?: string; minute?: string; ampm?: string }; to: { hour?: string; minute?: string; ampm?: string } }
    ): { from: { hour?: string; minute?: string; ampm?: string }; to: { hour?: string; minute?: string; ampm?: string } } => {
      const part = (p: TimeFields | { hour?: string; minute?: string; ampm?: string }) => ({
        hour: (typeof p.hour === 'string' ? p.hour : p.hour?.value) || '1',
        minute: (typeof p.minute === 'string' ? p.minute : p.minute?.value) || '00',
        ampm: (typeof p.ampm === 'string' ? p.ampm : p.ampm?.value) || 'AM',
      })
      return { from: part(time.from), to: part(time.to) }
    }

    const perDateTimes: Record<string, { from: { hour?: string; minute?: string; ampm?: string }; to: { hour?: string; minute?: string; ampm?: string } }> = {};
    payloadDateKeys.forEach((originalDateStr, index) => {
      const apiDateStr = formattedSelectedDates[index];
      const storedTime = perDateTimeStorage[originalDateStr];
      // See the perDateHours fallback above — same rationale applies to time.
      const originalTime = restrictToCommitted
        ? findByMatchingDate(initialTimeByDate, originalDateStr)
        : undefined;
      perDateTimes[apiDateStr] = toPayloadTime(storedTime || originalTime || selectedTime);
    });

    const payloadHours = birthdayChecked 
      ? { vacation: '0', float: '0', flexible: '0', other: '0' } 
      : hoursValues;

    // Merge any preserved (already-saved) draft dates so editing/adding a single
    // date via DraftView updates the draft in place instead of replacing it with
    // just the edited date. The active date's values win on key collisions
    // (spread last). When `preservedDatesPayload` is absent (every non-DraftView
    // caller), these reduce to the original single-flow values unchanged.
    const preserved = preservedDatesPayload;
    const mergedSelectedDates = preserved
      ? Array.from(new Set([...preserved.selectedDates, ...formattedSelectedDates]))
      : formattedSelectedDates;
    const mergedPerDateHours = preserved
      ? { ...preserved.perDateHoursValues, ...perDateHours }
      : perDateHours;
    const mergedPerDateDurations = preserved?.perDateDurations
      ? { ...preserved.perDateDurations, ...perDateDurations }
      : perDateDurations;
    const mergedPerDateTimes = preserved?.perDateTimes
      ? { ...preserved.perDateTimes, ...perDateTimes }
      : perDateTimes;

    // Build per-date Other subtype values for the active dates. Resolution hierarchy:
    // 1. Use per-date selection from perDateOtherSubTypeStorage (explicit per-date value)
    // 2. Fall back to global leaveTypeSelected['other'] when no per-date value is stored
    //    (e.g. all dates share the same Other type selected before clicking Next)
    const perDateOtherSubTypeValuesMap: Record<string, string> = {};
    payloadDateKeys.forEach((originalDateStr, index) => {
      const apiDateStr = formattedSelectedDates[index];
      // See the perDateHours fallback above — same rationale applies to the
      // Other-subtype selection.
      const originalSubType = restrictToCommitted
        ? findByMatchingDate(initialOtherSubTypeByDate, originalDateStr)
        : undefined;
      const perDateValue =
        perDateOtherSubTypeStorage[originalDateStr] || originalSubType || leaveTypeSelected['other'];
      if (perDateValue) {
        perDateOtherSubTypeValuesMap[apiDateStr] = perDateValue;
      }
    });
    // Merge preserved Other subtype values so dates NOT being edited in this session
    // keep their own saved subtype instead of inheriting the active session's global value.
    const mergedPerDateOtherSubTypeValues = preserved?.perDateOtherSubTypeValues
      ? { ...preserved.perDateOtherSubTypeValues, ...perDateOtherSubTypeValuesMap }
      : perDateOtherSubTypeValuesMap;

    const payload = {
      selectedDates: mergedSelectedDates,
      hoursValues: payloadHours,
      perDateHoursValues: birthdayChecked ? undefined : mergedPerDateHours,
      perDateDurations: birthdayChecked ? undefined : mergedPerDateDurations,
      perDateTimes: birthdayChecked ? undefined : mergedPerDateTimes,
      perDateOtherSubTypeValues: birthdayChecked ? undefined : mergedPerDateOtherSubTypeValues,
      isBirthday: birthdayChecked,
      isBirthdayRequest: isBirthdayRequestRef.current, // Persistent flag for birthday request
      originalBirthdayDate: originalBirthdayDateRef.current, // Which date is the birthday
      duration: selectedDuration as 'Full Day' | 'Partial Day',
      startTime,
      endTime,
      comments,
      // Thread the existing request id so editing a DRAFT updates it in place
      // instead of creating a new request; empty string means "create new".
      toReqId: isResubmitFlow ? (originalRequestId || '') : toReqId || '',
      otherSubType: leaveTypeSelected['other'],
      // Include admin-provided received date when in admin mode (YYYY-MM-DD)
      manualReqRcvdDate: isAdminMode ? formatManualRequestReceivedDate(adminReceivedDate) : undefined,
    }
    return payload;
  };
  // Unlike Next/Update (which validates the CURRENTLY active form entry
  // before letting the user commit it) and Submit (which must validate the
  // whole request), Save as Draft never persists the live/uncommitted form
  // state — buildSubmissionPayload(true) below restricts the payload to
  // `committedDates`, reading their hours from `perDateHoursStorage` (set
  // only once Next/Update has run and already validated). So there is
  // nothing here for validateAllHours() to usefully check: every committed
  // date is already known-valid, and the date currently open in the
  // picker/edit form — new or a live edit of an existing row — is not going
  // to be saved either way. Calling it anyway wrongly blocked Save as Draft
  // with "you need to enter hours" for an entry that was never going to be
  // submitted in the first place.
  // Extracted from handleSaveDraft purely to keep its Cognitive Complexity
  // under Sonar's threshold — same guards, same order, just relocated.
  const runSaveDraftPreflightChecks = (): boolean => {
    // Check for date gap exceeding 8 days on save — but, unlike the Next/Update
    // handler above (which validates a NEW entry against already-committed
    // dates before letting the user commit it), this must NOT include
    // `selectedDates` — the date currently open in the picker/edit form that
    // the user has not confirmed via Next/Update yet. Save as Draft only ever
    // persists `committedDates` (see buildSubmissionPayload(true) below), so
    // validating against an unconfirmed selection could block a save for an
    // entry that was never actually going to be saved in the first place.
    const saveDraftDatesForGapCheck = Array.from(new Set(committedDates || []))
    const saveDraftGapError = validateDateGaps(saveDraftDatesForGapCheck)
    if (saveDraftGapError) {
      onValidationError?.([saveDraftGapError])
      scrollToTopAndFocusAlert()
      return false
    }

    // Enforce the same comment requirements as final submission (e.g. Education
    // Time requires a comment) BEFORE hitting the API — otherwise the backend
    // rejects the payload and the user sees a raw API failure message instead
    // of a sensible, actionable validation banner.
    if (!validateCommentRequirements()) return false

    // Validate admin received date when in admin mode
    if (isAdminMode) {
      if (!adminReceivedDate.trim()) {
        setAdminReceivedDateError('Required')
        onValidationError?.(['Date Manual Request Received is required'])
        scrollToTopAndFocusAlert()
        return false
      }
      if (adminReceivedDateError) {
        onValidationError?.([adminReceivedDateError])
        scrollToTopAndFocusAlert()
        return false
      }
    }
    setAdminReceivedDateError('')
    setCommentError('')
    return true
  }

  // Extracted from handleSaveDraft's catch block purely to keep its Cognitive
  // Complexity under Sonar's threshold — same error handling, just relocated.
  const handleSaveDraftFailure = (error: unknown): void => {
    trackFormError(['save-exception'], 'time-off-request-draft')
    const apiError = extractApiError(error)
    if (apiError.status !== undefined && apiError.status >= 500) {
      // Any backend/gateway failure (500/502/503/504): send to parent
      // validation banner, don't display locally.
      onValidationError?.([apiError.message])
    } else {
      setSubmitError(error instanceof Error ? error.message : 'Error saving draft')
    }
  }

  const handleSaveDraft = async () => {
    if (!runSaveDraftPreflightChecks()) return

    // Build and submit payload — restrict to dates already committed to the
    // review table so a date still being added/edited (not yet confirmed via
    // Next/Update) is not saved into the draft.
    setSubmitLoading(true)
    setSubmitError('')
    try {
      const payload = buildSubmissionPayload(true)
      const result = await submitDraft(payload)

      if (result.success) {
        const timeOffRequestId = result.timeOffRequestId || ''
        trackFormSave('time-off-request-draft', { requestId: timeOffRequestId })
        setSubmitError('')
        setComments('')
        setShowHistoryTable(false)

        // Navigate to draft request page
        const statusPath = isAdminMode ? `/admin/requestor/status/${timeOffRequestId}` : `/requestor/status/${timeOffRequestId}`
        navigate(statusPath, {
          state: isAdminMode
            ? {
                employeeId,
                employeeName,
              }
            : undefined,
        })
      } else {
        trackFormError(['save-failed'], 'time-off-request-draft')
        // Pass error to parent via callback
        onValidationError?.([result.error || 'Failed to save draft'])
        scrollToTopAndFocusAlert()
      }
    } catch (error: unknown) {
      handleSaveDraftFailure(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  /**
   * Reads a from/to time field that may be a `TimeFields` dropdown option
   * (`{ value: '...' }`, from local per-date state) or a plain string (from
   * `preservedDatesPayload.perDateTimes`, which stores already-resolved API
   * values). Falls back to `fallback` when neither shape yields a value.
   */
  const readTimeFieldValue = (field: unknown, fallback: string): string => {
    if (field && typeof field === 'object') {
      return (field as { value?: string }).value || fallback
    }
    return (field as string) || fallback
  }

  /**
   * Builds the time-entry rows used in the post-submit navigation state.
   * Each row describes the date, duration, hours, and request type for review.
   *
   * Iterates over every date in the submitted request — not just `sortedDates`
   * (this session's active/edited dates) — by also including the OTHER dates
   * carried in `preservedDatesPayload` (the rest of a multi-day draft that
   * DraftView isn't currently editing). Without this, editing one row of a
   * multi-day draft and submitting would only produce a row for that one date,
   * even though `buildSubmissionPayload` correctly submits every date.
   */
  const buildTimeEntries = () => {
    const preserved = preservedDatesPayload
    const preservedDisplayDates = (preserved?.selectedDates || []).map(formatApiDateForDisplay)
    const allDates = Array.from(new Set([...sortedDates, ...preservedDisplayDates])).sort(
      (a, b) => normalizeDateKey(a).localeCompare(normalizeDateKey(b))
    )

    return allDates.map((date) => {
      const isoKey = normalizeDateKey(date)
      const dayName = new Date(date).toLocaleDateString('en-US', { weekday: 'long' })
      const formattedDate = formatDateForDisplay(date)

      const dateHours =
        perDateHoursStorage[date] || preserved?.perDateHoursValues?.[isoKey] || hoursValues
      const dateTotal = Object.values(dateHours).reduce(
        (sum, h) => sum + (Number.parseFloat(h) || 0),
        0
      )

      const dateDuration =
        perDateDurationStorage[date] || preserved?.perDateDurations?.[isoKey] || selectedDuration
      const dateTime = perDateTimeStorage[date] || preserved?.perDateTimes?.[isoKey] || selectedTime

      let timesRequested = 'Full Day'
      if (dateDuration === 'Partial Day') {
        const fromHour = readTimeFieldValue(dateTime.from?.hour, '1')
        const fromMin = readTimeFieldValue(dateTime.from?.minute, '00')
        const fromAmPm = readTimeFieldValue(dateTime.from?.ampm, 'AM')
        const toHour = readTimeFieldValue(dateTime.to?.hour, '1')
        const toMin = readTimeFieldValue(dateTime.to?.minute, '00')
        const toAmPm = readTimeFieldValue(dateTime.to?.ampm, 'AM')
        timesRequested = `${fromHour}:${fromMin}${fromAmPm.toLowerCase()} - ${toHour}:${toMin}${toAmPm.toLowerCase()}`
      }

      // Preserve the Birthday identity for the birthday date in a multi-date request.
      // `birthdayChecked` is auto-unchecked once a second date is added, so it cannot
      // be relied on here; the persistent birthday refs must be consulted instead —
      // matching the review-table builders (buildReviewRows). Without this, the birthday
      // date would fall through to determineRequestType and render as Vacation (PTO/ETO)
      // in the post-submit navigation state.
      const isOriginalBirthdayDate = !!(
        isBirthdayRequestRef.current &&
        date === originalBirthdayDateRef.current &&
        originalBirthdayRequestTypeRef.current
      )
      const effectiveOtherSubType =
        perDateOtherSubTypeStorage[date] || preserved?.perDateOtherSubTypeValues?.[isoKey]
      const effectiveLeaveTypeForDate = effectiveOtherSubType
        ? { ...leaveTypeSelected, other: effectiveOtherSubType }
        : leaveTypeSelected
      const dateRequestType = isOriginalBirthdayDate
        ? { displayType: originalBirthdayRequestTypeRef.current }
        : determineRequestType(dateHours, effectiveLeaveTypeForDate, birthdayChecked)
      // Birthday leave is a fixed full-day type with no user-entered hours.
      const hours = birthdayChecked || isOriginalBirthdayDate ? 'NA' : dateTotal.toFixed(2)

      return {
        requestType: dateRequestType?.displayType || 'Vacation (PTO/ETO)',
        date: formattedDate,
        day: dayName,
        timesRequested,
        hours,
        hoursOtherRequests: formatOtherRequestBreakdown(
          getOtherRequestBreakdownForDate(date, otherRequestHoursByDate)
        ),
      }
    })
  }

  /**
   * Navigates to the appropriate page after a successful final submission.
   * Resubmit flow goes to the status page; create flow goes to request details.
   */
  const navigateAfterSubmit = (
    timeOffRequestId: string,
    timeEntries: ReturnType<typeof buildTimeEntries>,
    userName: string,
    userNuid: string,
    submittedOn: string,
    submittedDates: string[],
  ) => {
    const requestPath = isAdminMode ? '/admin/requestor/request' : '/requestor/request'
    if (isResubmitFlow) {
      const statusPath = isAdminMode
        ? `/admin/requestor/status/${timeOffRequestId}`
        : `/requestor/status/${timeOffRequestId}`
      navigate(statusPath, {
        state: isAdminMode ? { employeeId, employeeName } : undefined,
      })
    } else {
      navigate(requestPath, {
        state: {
          // Use the dates actually submitted in the payload (`submittedDates`,
          // API `YYYY-MM-DD` strings), not `sortedDates`/`selectedDates` — those
          // only reflect the row currently open for editing, so a multi-day
          // draft would collapse to a single date here after editing one row.
          selectedDates: [...submittedDates].sort((a, b) => a.localeCompare(b)).map(formatApiDateForDisplay),
          timeEntries,
          timeOffRequestId,
          userName,
          userNuid,
          submittedOn,
          comments,
          ...(isAdminMode && { employeeId, employeeName,adminReceivedDate }),
        },
      })
    }
  }

  /**
   * Validates that all comment requirements are met before final submission.
   * Returns true when comments are valid; false when a comment error was set
   * and the caller should abort.
   */
  const validateCommentRequirements = (): boolean => {
    const focusCommentsField = () => {
      const el = document.getElementById('comments-textarea')
      if (el) el.focus()
      scrollToTopAndFocusAlert(0)
    }
    if (isEducationOtherRequest() && comments.trim() === '') {
      setCommentError('Required')
      onValidationError?.([ValidationMessages.EDUCATION_COMMENT_REQUIRED])
      focusCommentsField()
      return false
    }
    if (isResubmitFlow && comments.trim() === '') {
      setCommentError('Required')
      onValidationError?.(['Resubmitted comments are required'])
      focusCommentsField()
      return false
    }
    return true
  }

  // Extracted from handleSubmitRequest purely to keep its Cognitive Complexity
  // under Sonar's threshold — same guards, same order, just relocated.
  const runSubmitPreflightChecks = (): boolean => {
    const bannerMessages = validateAllHours()

    if (bannerMessages) {
      trackFormError(['hours-validation'], isVacationBidRequest ? 'vacation-bid' : 'time-off-request')
      setShowHistoryTable(false)
      scrollToTopAndFocusAlert()
      return false
    }

    // Check for date gap exceeding 8 days on submit — same check, same date
    // set (committedDates ∪ selectedDates), as the Next/Update handler above,
    // so a gap that would have been blocked there is also blocked here before
    // the API call is made. Applies to every request type.
    const submitDatesForGapCheck = Array.from(
      new Set([...(committedDates || []), ...selectedDates])
    )
    const submitGapError = validateDateGaps(submitDatesForGapCheck)
    if (submitGapError) {
      onValidationError?.([submitGapError])
      scrollToTopAndFocusAlert()
      return false
    }

    if (!validateCommentRequirements()) return false

    // Validate admin received date when in admin mode
    if (isAdminMode) {
      if (!adminReceivedDate.trim()) {
        setAdminReceivedDateError('Required')
        onValidationError?.(['Date Manual Request Received is required'])
        scrollToTopAndFocusAlert()
        return false
      }
      if (adminReceivedDateError) {
        onValidationError?.([adminReceivedDateError])
        scrollToTopAndFocusAlert()
        return false
      }
    }
    setAdminReceivedDateError('')
    setCommentError('')
    return true
  }

  // Extracted from handleSubmitRequest's success branch purely to keep its
  // Cognitive Complexity under Sonar's threshold — same logic, just relocated.
  const handleSubmitSuccess = (
    result: { success: boolean; timeOffRequestId?: string },
    payload: ReturnType<typeof buildSubmissionPayload>
  ): void => {
    const timeOffRequestId = result.timeOffRequestId || ''

    let formType = 'time-off-request'
    if (isVacationBidRequest) {
      formType = 'vacation-bid'
    } else if (isResubmitFlow) {
      formType = 'resubmit'
    }

    trackFormSubmit(
      formType,
      { requestId: timeOffRequestId }
    )

    const sessionNuid = sessionStorage.getItem('userId') || sessionStorage.getItem('nuid')
    const sessionUserName = sessionStorage.getItem('userName')
    const userNuid = loggedInUserNuid || sessionNuid || ''
    const userName = loggedInUserName || sessionUserName || ''
    const submittedOn = new Date().toLocaleString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })

    // Build time entry payload for review using actual submitted per-date hours
    const timeEntries = buildTimeEntries()

    // Navigate to the correct page based on flow type and role
    navigateAfterSubmit(timeOffRequestId, timeEntries, userName, userNuid, submittedOn, payload.selectedDates)
  }

  // Extracted from handleSubmitRequest's catch block purely to keep its
  // Cognitive Complexity under Sonar's threshold — same error handling, just relocated.
  const handleSubmitFailure = (error: unknown): void => {
    trackFormError(['submit-exception'], isVacationBidRequest ? 'vacation-bid' : 'time-off-request')
    const apiError = extractApiError(error)
    if (apiError.status !== undefined && apiError.status >= 500) {
      // Any backend/gateway failure (500/502/503/504): send to parent
      // validation banner, don't display locally.
      onValidationError?.([apiError.message])
    } else {
      setSubmitError(error instanceof Error ? error.message : 'Error submitting request')
    }
    scrollToTopAndFocusAlert()
  }

  const handleSubmitRequest = async () => {
    if (!runSubmitPreflightChecks()) return

    // Submit request to API
    setSubmitLoading(true)
    setSubmitError('')
    try {
      const payload = buildSubmissionPayload()
      // Use submitResubmitted for resubmission flow, otherwise use submitFinal
      const submitMethod = isResubmitFlow ? submitResubmitted : submitFinal
      const result = await submitMethod(payload)

      if (result.success) {
        handleSubmitSuccess(result, payload)
      } else {
        // Pass error to parent via callback
        trackFormError(['submit-failed'], isVacationBidRequest ? 'vacation-bid' : 'time-off-request')
        onValidationError?.([result.error || 'Failed to submit request'])
        scrollToTopAndFocusAlert()
      }
    } catch (error: unknown) {
      handleSubmitFailure(error)
    } finally {
      setSubmitLoading(false)
    }
  }
 
  /**
   * Renders the partial-day time-range dropdowns (from/to hour, minute, AM/PM).
   * Returns null when the selected duration is not "Partial Day".
   * Extracted as a named function so SonarQube analyses its complexity separately.
   */
  const renderPartialDayTimeSection = () => {
    if (selectedDuration !== 'Partial Day') return null
    // Pre-bind onSelect at this level so it never exceeds 4 levels of function nesting
    const onSelectField = (side: 'from' | 'to', name: keyof TimeFields) =>
      (opt: DropdownOption) => handleTimeSelect(side, name, opt)
    return (
      <>
        <p className='p3 -medium pt-4'>Time ({formatTime(selectedTime.from)} - {formatTime(selectedTime.to)})</p>
        <div className='flex flex-col gap-4 mt-2'>
          {(['from', 'to'] as const).map((sideLabel) => {
            const label = sideLabel === 'from' ? 'Start Time' : 'End Time';
            const fields = [
              { name: 'hour' as const, opts: hoursOptions, placeholder: '1', label: 'Hour' },
              { name: 'minute' as const, opts: minutesOptions, placeholder: '00', label: 'Minutes' },
              { name: 'ampm' as const, opts: ampmOptions, placeholder: 'AM', label: 'AM/PM' },
            ];

            return (
              <div key={sideLabel} className='flex flex-col gap-2'>
                <p className='p3 -book'>{label}:</p>
                <div className='flex gap-2 items-center'>
                  {fields.map((f) => (
                    <Dropdown
                      key={f.name}
                      options={f.opts}
                      selectedValue={selectedTime[sideLabel][f.name]}
                      onSelect={onSelectField(sideLabel, f.name)}
                      placeholder={f.placeholder}
                      menuClassName='max-w-[100px]'
                      itemClassName='p-2'
                      label={f.label}
                      minWidth='100px'
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </>
    )
  }

  /**
   * Renders the birthday checkbox, its "already taken" notice, the divider, and
   * the hours-summary paragraph (or "Birthday Requested" text when checked).
   * Extracted as a named function so SonarQube analyses its complexity separately.
   */
  const renderBirthdayAndHoursSection = () => (
    <>
      {isBirthdayEnabled && (
        <><hr className='-variation-1' /><div className='flex flex-col'>
          <div className='flex items-center gap-3 my-2'>
            <input
              id="birthday-checkbox"
              name="birthday"
              type="checkbox"
              className="checkbox"
              checked={birthdayChecked || isBirthdayTaken}
              onChange={(e) => {
                setBirthdayChecked(e.target.checked)
                // Persistent flag: mark this as a birthday request if checked
                if (e.target.checked) {
                  isBirthdayRequestRef.current = true
                  // In edit mode anchor the birthday to the date being edited
                  // (not selectedDates[0], which may be a different date in a
                  // multi-date request); restore its 'Birthday' review type.
                  originalBirthdayDateRef.current =
                    (isInDateEditMode && editDate) ? editDate : (selectedDates[0] || null)
                  originalBirthdayRequestTypeRef.current = 'Birthday'
                } else {
                  // Unchecking converts the date back into a normal hours entry.
                  // Clear the birthday tracking for the affected date so the review
                  // table reflects the entered hours/type instead of the stale
                  // "Birthday" label (which is keyed on originalBirthdayDate).
                  const affectedDate = isInDateEditMode ? editDate : selectedDates[0]
                  if (datesMatch(affectedDate, originalBirthdayDateRef.current)) {
                    originalBirthdayDateRef.current = null
                    originalBirthdayRequestTypeRef.current = null
                  }
                  isBirthdayRequestRef.current = false
                }
              }}
              disabled={isBirthdayCheckboxDisabled}
              title={getBirthdayCheckboxTooltip(isBirthdayTaken, selectedDates.length, selectedDuration, perDayRequested)} />
            <label
              htmlFor="birthday-checkbox"
              className={`p3 -book ${isBirthdayCheckboxDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              Birthday
            </label>
          </div>

          {isBirthdayTaken && (
            <div className='flex items-center gap-2 text-sm text-gray-600 mb-2'>
              <img src="/assets/images/informationIcon.svg" alt="info" className="w-4 h-4" />
              <p className='p3 -book'>Your birthday off has already been taken for this year.</p>
            </div>
          )}

          {/* Note: info message is now shown when birthday is checked above */}
        </div></>
      )}
      <hr className='-variation-1'/>
      {birthdayChecked ? (
        <p className='p2 -book'>Birthday Requested for the selected Day</p>
      ) : (
        <>
          <p  className='p2 -book'>{formatHours(perDayRequested)} Hours requested per day</p>
          <p  className='p2 -book'>{formatHours(totalRequested)} Hours total</p>
        </>
      )}
    </>
  )

  /**
   * Handles Back button click from edit form.
   * Removes newly added dates with 0 hours and cleans up their storage entries.
   * Issue 1 Fix: Prevents unsaved new dates from appearing in the review table.
   */
  const handleBackFromEditForm = () => {
    const datesToRemove: string[] = [];
    selectedDates.forEach((date) => {
      // Use only the last committed value, never the live in-progress edit —
      // Back must discard whatever was typed but never confirmed via
      // Next/Update (e.g. an invalid >16 hour entry that failed validation
      // and was therefore never written to perDateHoursStorage).
      const hoursForDate = perDateHoursStorage[date] || {}

      // Check if this date has 0 hours and wasn't in the original committed dates
      const totalHours = Object.values(hoursForDate).reduce(
        (sum, h) => sum + (Number.parseFloat(h as string) || 0),
        0
      );

      // Remove if it's a newly added date (not committed) with 0 hours
      if (totalHours === 0 && !committedDates?.includes(date)) {
        datesToRemove.push(date);
      }
    });

    // Remove newly added dates with 0 hours from both selectedDates and storage
    if (datesToRemove.length > 0) {
      if (onRemoveDatesClick) {
        // Use bulk removal to avoid stale-closure issues
        onRemoveDatesClick(datesToRemove);
      } else {
        // Fallback to individual removal
        datesToRemove.forEach((date) => onRemoveDateClick?.(date));
      }
      
      // Also clean up their storage entries
      setPerDateHoursStorage((prev) => {
        const updated = { ...prev };
        datesToRemove.forEach((date) => delete updated[date]);
        return updated;
      });
      setPerDateDurationStorage((prev) => {
        const updated = { ...prev };
        datesToRemove.forEach((date) => delete updated[date]);
        return updated;
      });
      setPerDateTimeStorage((prev) => {
        const updated = { ...prev };
        datesToRemove.forEach((date) => delete updated[date]);
        return updated;
      });
      setPerDateOtherSubTypeStorage((prev) => {
        const updated = { ...prev };
        datesToRemove.forEach((date) => delete updated[date]);
        return updated;
      });
    }
    
    // Clear any validation error left over from a failed Next/Update click so
    // the banner and field-level "Revise" markers don't persist into the
    // review table after the invalid, uncommitted entry has been discarded.
    setFieldErrors({});
    setIsNextClicked(false);
    lastValidationMessagesRef.current = null;
    onValidationError?.(null);

    onBackFromEditMode?.();
    setShowHistoryTable(true);
  };

  /**
   * Renders the form's action buttons (Back/Cancel/Update in edit mode, or
   * Cancel/Next in normal mode).
   * Extracted as a named function so SonarQube analyses its complexity separately.
   */
  const renderActionButtons = () => {
    if (isInDateEditMode && !isFullDayDisabled) {
      return (
        <>
          <button
            type="button"
            className="button -light block w-full! desktop:w-auto!"
            onClick={handleBackFromEditForm}
          >
            Back
          </button>
          <button
            type="button"
            className="button -light block w-full! desktop:w-auto!"
            onClick={() => setShowCancelModal(true)}
          >
            Cancel Request
          </button>
          <button
            type="button"
            className="button -primary -light block w-full! desktop:w-auto!"
            onClick={handleNextOrUpdateClick}
          >
            Update
          </button>
        </>
      )
    }
    return (
      <>
        <button
          type="button"
          className="button -light block w-full! desktop:w-auto!"
          onClick={() => setShowCancelModal(true)}
        >
          Cancel Request
        </button>
        <button
          type="button"
          className="button -primary -light block w-full! desktop:w-auto!"
          onClick={handleNextOrUpdateClick}
        >
          Next: Review Request
        </button>
      </>
    )
  }

  /**
   * Renders a single leave-type row (hours input, type selector, resulting balance).
   * Extracted as a named function so SonarQube analyses its complexity separately
   * from the component body.
   */
const renderLeaveRow = (
  r: { 
    id: keyof HoursValues
    label: string
    showDropdown: boolean 
  }
) => {
    
    // Display hours: use editing hours if in edit mode, otherwise use global hours
    const displayedHours = isInDateEditMode
      ? currentEditingDateHours[r.id] ?? ''
      : (hoursValues[r.id] ?? '')

    // Get the deducted balance (available - hours entered)
        const remaining = deductedBalances[r.id as keyof typeof deductedBalances] || 'N/A'
          const hasFloatSentinelUnavailable = isFloatSentinelUnavailable(r.id)
          const isBalanceUnavailable = hasFloatSentinelUnavailable || String(remaining).toUpperCase() === 'N/A'

              // Float is the only leave type that surfaces an info tooltip when its
              // resulting balance is unavailable.
              const isFloatBalanceUnavailable = r.id === 'float' && isBalanceUnavailable

              // Normalize "N/A" to "NA" to match the design for balance-backed types.
              const normalizedBalance = isBalanceUnavailable ? 'NA' : remaining
              // "Other Types of Time Off" is a category selection (jury duty,
              // bereavement, etc.), not a balance-backed leave type, so it has no
              // resulting balance to show — render nothing for it (no label) per design.
              const displayBalance = r.id === 'other' ? '' : normalizedBalance

    const hoursForDisableLogic = isInDateEditMode ? currentEditingDateHours : hoursValues

    const flexibleHours = Number.parseFloat(hoursForDisableLogic.flexible || '0') || 0
    const isDisabledByFlexible = flexibleHours > 0 && (r.id === 'vacation' || r.id === 'float' || r.id === 'other')

    const vacationHours = Number.parseFloat(hoursForDisableLogic.vacation || '0') || 0
    const floatHours = Number.parseFloat(hoursForDisableLogic.float || '0') || 0
    const otherHours = Number.parseFloat(hoursForDisableLogic.other || '0') || 0
    const isDisabledByOthers = (vacationHours > 0 || floatHours > 0 || otherHours > 0) && r.id === 'flexible'

    // The no-hours marker ('Required') is only meaningful immediately after an
    // explicit Next/Update click. `isNextClicked` resets whenever the selected
    // dates or active edit date change, so gating the marker on it guarantees a
    // stale "Required" inline error can never linger when the user switches to
    // or adds another date — independent of effect timing (HRCOM-104959).
    // Duration/balance errors ('Revise') are always shown.
    const rawFieldError = fieldErrors[r.id]
    const rowError =
      rawFieldError === 'Required' && !isNextClicked ? undefined : rawFieldError
    const shouldHighlightOtherSubtype = r.id === 'other' && isOtherSubtypeDropdownHighlighted

    return (
      <div
        key={r.id}
        className="flex flex-col md:flex-row md:gap-x-2 items-center pt-2 md:pt-3 mb-2 pb-2"
      >
        <div className="w-full md:w-1/3 px-0 mb-6!">
          <TextInputField
            label="Hours Per Day"
            id={r.id}
            name={r.id}
            value={displayedHours}
            onChange={(e) => {
              const value = e.target.value

              // Accept only numeric values with one optional decimal point.
              if (isValidHoursInput(value)) {
                updateLeaveTypeHours(r.id, value)
              }
            }}
            onFocus={() => clearFieldError(r.id)}
            placeholder="0.00"
            type="text"
            error={rowError}
            customStyles={{ width: '130px' }}
            disabled={
              isDisabledByFlexible || isDisabledByOthers || birthdayChecked
            }
          />
        </div>
        <div className="w-full md:w-1/3 flex flex-col items-start md:justify-start px-0 min-w-0">
          {r.showDropdown ? (
            (() => {
              // Use the editMode prop (not derived state) so the check is never stale
              // across render cycles when edit mode is activated by the parent.
              const inEditMode = (editMode || isInDateEditMode) && !!editDate
              // editDate is guaranteed non-null when inEditMode is true. Use the
              // resolved (storage-key-matching) form — editDate itself can arrive
              // as MM/DD/YYYY from the review table's edit icon while storage is
              // keyed by the raw selectedDates format.
              const safeEditDate = resolvedEditDate ?? ''
              // For brand-new (not-yet-committed) dates, fall back to the global
              // leaveTypeSelected so a previously chosen subtype carries over.
              // For existing committed dates, never fall back: once the per-date
              // storage entry is cleared (e.g. Other hours removed and Update
              // clicked), the dropdown must reset to empty rather than showing
              // the stale global selection. Without this guard, partial-day
              // requests retain the old subtype because their leaveTypeSelected
              // is not cleared on edit-mode entry (unlike full-day requests
              // where setLeaveTypeSelected({}) runs via the exitAddTimeReviewSignal
              // A date is "committed" (reviewed) if it has per-date hours OR a
              // per-date duration entry. The birthday date is intentionally kept
              // out of hours storage but IS added to duration storage, so this
              // second check ensures the birthday date is treated as existing and
              // never falls back to the global subtype (which would leak another
              // date's Other selection into it).
              const isExistingDate = safeEditDate
                ? !!perDateHoursStorage[safeEditDate] ||
                  !!perDateDurationStorage[safeEditDate]
                : false
              const fallbackSubType = isExistingDate
                ? undefined
                : leaveTypeSelected[r.id]
              const activeSubType = inEditMode
                ? (perDateOtherSubTypeStorage[safeEditDate] ?? fallbackSubType)
                : leaveTypeSelected[r.id]
              return (
                <div
                  ref={r.id === 'other' ? otherSubtypeDropdownRef : undefined}
                  className={shouldHighlightOtherSubtype ? 'rounded-lg ring-2 ring-[#0074AD] ring-offset-2 bg-[#EAF6FD] px-2 py-2 transition-all duration-300' : 'transition-all duration-300'}
                  style={r.id === 'other' ? { scrollMarginTop: '96px' } : undefined}
                >
                  <Dropdown
                    options={otherLeaveOptions}
                    selectedValue={otherLeaveOptions.find(
                      (o) => o.value === activeSubType
                    )}
                    onSelect={(opt) => handleLeaveTypeSelect(opt, r.id)}
                    placeholder="Select an option"
                    menuClassName="max-w-full always-visible-scrollbar"
                    itemClassName="p-2"
                    label={r.label}
                    disabled={isDisabledByFlexible || birthdayChecked}
                  />
                </div>
              )
            })()
          ) : (
            <>
              <p className="p3 -book text-sm md:text-base m-0">{r.label}</p>
              {r.id === 'flexible' && isDisabledByOthers && (
                <div className="text-sm text-gray-600 mt-1 flex items-start gap-2" id="fpd-combine-info">
                  <img
                    src="/assets/images/info-circle-small-outline.svg"
                    alt="info"
                    className="w-4 h-4 mt-0.5 shrink-0"
                  />
                  <span>Flexible Personal Days Cannot be combined with other time off types.</span>
                </div>
              )}
            </>
          )}
          {r.showDropdown &&
            (() => {
              // Snapshotted at validation time (set by validateOtherRules into
              // fieldErrors.otherSubtype), not recomputed live on every keystroke —
              // otherwise this would pop up mid-typing instead of only alongside
              // the matching banner, the same way every other inline marker works.
              const showOtherSubtypeInline =
                isNextClicked && fieldErrors['otherSubtype'] === 'Required'
              return showOtherSubtypeInline ? (
                <div
                  className="text-sm text-red-600 mt-1 flex items-center gap-2"
                  id="other-subtype-error"
                >
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="error"
                    className="w-4 h-4"
                  />
                  <span>Required</span>
                </div>
              ) : null
            })()}
        </div>
        <div className="w-full md:w-1/3 flex items-center md:justify-start px-0">
          <div className="flex items-center gap-2">
            {displayBalance ? (
              <p className="p3 -book text-sm md:text-base">{displayBalance}</p>
            ) : null}
            {isFloatBalanceUnavailable && (
              <>
                {/* Desktop / sm+: render above the icon */}
                <div className="hidden sm:block">
                  <TooltipBanner
                    header="Float balance is not available"
                    message="Float balance data is not available. Check with your manager for your Float balance."
                    tooltipContainerClass="left-1/2 top-0 -translate-x-1/2 -translate-y-full mb-2"
                    showOnHover={true}
                  >
                    <button
                      type="button"
                      aria-label="Float info"
                      className="w-5 h-5 flex items-center justify-center border-0 p-0 bg-transparent cursor-pointer"
                    >
                      <img
                        src="/assets/images/info-circle-small-outline.svg"
                        alt="info"
                        className="w-4 h-4"
                      />
                    </button>
                  </TooltipBanner>
                </div>

                {/* Mobile: render inline/relative so it stays near the trigger and not fixed to viewport */}
                <div className="block sm:hidden">
                  <TooltipBanner
                    header="Float balance is not available"
                    message="Float balance data is not available. Check with your manager for your Float balance."
                    tooltipContainerClass="relative px-4 w-full"
                    showOnHover={true}
                  >
                    <button
                      type="button"
                      aria-label="Float info"
                      className="w-5 h-5 flex items-center justify-center border-0 p-0 bg-transparent cursor-pointer"
                    >
                      <img
                        src="/assets/images/info-circle-small-outline.svg"
                        alt="info"
                        className="w-4 h-4"
                      />
                    </button>
                  </TooltipBanner>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // DraftView (hideReviewTable) landing state: no date selected/open yet.
  // Show the comment section + Cancel / Save as Draft / Submit Request
  // buttons immediately instead of waiting for `showHistoryTable` (only set
  // true after "Next"/"Update"), so the user can save/submit the draft as-is
  // without first opening a date. Every other AddTimeToRequest caller always
  // has a date selected, so this is a no-op for them.
  const showActionsWithoutHistory = !!hideReviewTable && selectedDates.length === 0

  // Renders the "Requesting Time Off Hours For" header and date-list summary.
  // Extracted from the component's JSX purely to keep the component's own
  // Cognitive Complexity under Sonar's threshold — same markup, just relocated
  // into its own function (Sonar scores each function separately).
  const renderRequestingForSection = () => (
    <>
      <h2 className="h2 mb-2">
        {editMode && !isFullDayDisabled
          ? 'Edit your request'
          : 'Request Time Off'}
      </h2>
      <p className="p3 -medium mb-2">Requesting Time Off Hours For:</p>
      {editMode && !isFullDayDisabled ? (
        <div className="pt-2">
          {selectedDates && selectedDates.length > 0 && (
            <>
              {/* Filter committedDates to only show dates currently in selectedDates
      This prevents removed dates from appearing in the edit flow */}
              {(() => {
                const activeCommittedDates = (
                  committedDates || []
                ).filter((d) => selectedDates.includes(d))
                const activeAddingDates =
                  sortedCurrentlyAddingDates.filter((d) =>
                    selectedDates.includes(d)
                  )

               return isAddingMoreDates ? (
  <>
    {activeCommittedDates.length > 0 && (
      <ul className="list-disc pl-6 pt-2 ml-6!">
        {activeCommittedDates
          .slice()
          .sort(
            (a, b) =>
              new Date(a).getTime() - new Date(b).getTime()
          )
          .map((date) => (
            <li
              key={date}
              className="p3 -book text-lg mb-1"
            >
              {formatDateForDisplay(date)}
            </li>
          ))}
      </ul>
    )}

    {activeAddingDates.length > 0 && (
      <>
        <p className="p3 -medium mt-3 mb-2">
          Currently adding:
        </p>
        <ul className="list-disc pl-6 pt-1 ml-6!">
          {activeAddingDates.map((date) => (
            <li
              key={date}
              className="p3 -book text-lg mb-1"
            >
              {formatDateForDisplay(date)}
            </li>
          ))}
        </ul>
      </>
    )}
  </>
 ) : (
  editDate
);
              })()}
            </>
          )}
        </div>
      ) : (
        <>
          {committedDates && committedDates.length > 0 ? (
            <>
              <ul className="list-disc pl-6 pt-2 ml-6!">
                {sortedCommittedDates.map((date) => (
                  <li key={date} className="p3 -book">
                    {formatDateForDisplay(date)}
                  </li>
                ))}
              </ul>
              {sortedCurrentlyAddingDates.length > 0 && (
                <>
                  <p className="p3 -medium mt-3 mb-2">
                    Currently adding:
                  </p>
                  <ul className="list-disc pl-6 pt-1 ml-6!">
                    {sortedCurrentlyAddingDates.map((date) => (
                      <li key={date} className="p3 -book">
                        {formatDateForDisplay(date)}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <ul className="list-disc pl-6 pt-2 ml-6!">
              {sortedDates.map((date) => (
                <li key={date} className="p3 -book">
                  {formatDateForDisplay(date)}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  )

  // Renders the review table, comments, admin fields, API error banner, and
  // Cancel/Save Draft/Submit (or Resubmit) buttons shown once the user has
  // clicked Next/Update (or in DraftView's landing state).
  // Extracted from the component's JSX purely to keep the component's own
  // Cognitive Complexity under Sonar's threshold — same markup, just relocated
  // into its own function (Sonar scores each function separately).
  const renderReviewAndSubmitSection = () => (
    <div className="mt-8">
      {!hideReviewTable && !isResubmitFlow && (
        <>
          <h2 className="h2 mb-2">Review Your Request</h2>
          <p className="p3 -book mb-4">
            To add a date, select the Add date to request button. To edit or remove a date, you may use the table below.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="button -secondary block w-full! desktop:w-auto!"
              onClick={onAddMoreDates}
              disabled={submitLoading}
            >
              Add date to request
            </button>
          </div>
        </>
      )}
      {!hideReviewTable && (
        <ReviewTable
          selectedDates={selectedDates}
          hoursValues={hoursValues}
          leaveTypeSelected={leaveTypeSelected}
          selectedDuration={selectedDuration}
          selectedTime={selectedTime}
          isBirthdayChecked={birthdayChecked}
          calculateHoursForDate={calculatePerDateTotalHours}
          perDateDurationStorage={perDateDurationStorage}
          perDateTimeStorage={perDateTimeStorage}
          perDateHoursStorage={perDateHoursStorage}
          perDateOtherSubTypeStorage={perDateOtherSubTypeStorage}
          originalBirthdayDate={originalBirthdayDateRef.current}
          originalBirthdayRequestType={
            originalBirthdayRequestTypeRef.current
          }
          otherRequestHoursByDate={otherRequestHoursByDate}
          onEditDate={(date) => {
            onEditDateClick?.(date)
          }}
          onRemoveDate={(date) => {
            onRemoveDateFromReviewTable(date)
          }}
        />
      )}

      {/* Comments Section */}
      <div className="form-group margin-top-2u mt-8">
        <label
          id="comments-label"
          className="p4 -medium"
          htmlFor="comments-textarea"
        >
          Comments
          {(isEducationOtherRequest() || isResubmitFlow) && (
            <span className="text-red-600 ml-1">*</span>
          )}
        </label>
        <div className="textarea-wrapper w-[60%] relative">
          <textarea
            autoComplete="on"
            id="comments-textarea"
            name="comments"
            aria-labelledby="comments-label comments-count"
            aria-invalid={!!commentError}
            aria-describedby={commentError ? 'comments-error' : undefined}
            className={`text-area_word-count text-area_global -book text-base! text-[#0D1C3D]! w-full! ${commentError ? 'border-2 border-red-600' : ''}`}
            style={
              commentError
                ? {
                    border: '2px solid #DC2626',
                    boxShadow: '0 0 0 3px rgba(220,38,38,0.12)',
                    boxSizing: 'border-box',
                  }
                : undefined
            }
            data-show-lines="3"
            rows={3}
            maxLength={1000}
            onChange={(e) => {
              setComments(e.target.value)
              if (commentError && e.target.value.trim() !== '') {
                setCommentError('')
                onValidationError?.(null)
              }
            }}
            onFocus={() => {
              if (commentError) {
                setCommentError('')
                onValidationError?.(null)
              }
            }}
            value={comments}
          ></textarea>
          <div className="character-count">
            <span
              id="comments-count"
              aria-live="polite"
              aria-atomic="true"
              className="count-area text-input-helper"
              data-description=" of 1000 characters left"
              data-full-text="No more characters available, 1000 of 1000 characters left"
            >
              {1000 - comments.length} of 1000 characters left
            </span>
          </div>
          {commentError && (
            <div id="comments-error" className="mt-2">
              <ValidationErrorDisplay message={commentError} />
            </div>
          )}
        </div>
      </div>
      {isAdminMode && (
        <>
          <ManualRequestedDate
            name="admin-datepicker"
            label="Date Manual Request Received"
            value={adminReceivedDate}
            onChange={(v) => {
              setAdminReceivedDate(v)
              if (!v.trim()) {
                setAdminReceivedDateError('')
                return
              }
              const parsed = new Date(v)
              const today = new Date()
              today.setHours(23, 59, 59, 999)
              if (!Number.isNaN(parsed.getTime()) && parsed > today) {
                setAdminReceivedDateError(
                  'Future dates are not allowed for Date Manual Request Received.'
                )
              } else {
                setAdminReceivedDateError('')
              }
            }}
            required={true}
            className="mt-10! w-auto max-w-xs"
            disableFutureDates={true}
            error={adminReceivedDateError}
          />
          <LabeledValue
            label="Submitted by Requestor Proxy"
            value={(() => {
              const manualEmployee = JSON.parse(
                sessionStorage.getItem('manualTimeOffEmployee') || '{}'
              )
              return `${manualEmployee?.admin?.adminName || 'Employee'} (${manualEmployee?.admin?.adminNuid || 'Unknown'})`
            })()}
          />
        </>
      )}

      {/* API Error Display */}
      {submitError && (
        <ValidationBanner
          variant="error"
          heading="Error"
          message={submitError}
          compact={true}
        />
      )}

      <div className="flex flex-col-reverse desktop:flex-row gap-3 mt-6! mb-8!">
        {isResubmitFlow ? (
          <>
            <button
              type="button"
              className="button -secondary block w-full! desktop:w-auto!"
              onClick={() =>
                navigate(`${isAdminMode ? '/admin' : ''}/requestor/status/${originalRequestId}`)
              }
              disabled={submitLoading}
            >
              Back
            </button>

            <button
              type="button"
              className={`button block w-full! desktop:w-auto! ${hasEditsBeenMade ? '-secondary' : '-primary -light'}`}
              onClick={() => setShowCancelModal(true)}
              disabled={submitLoading || hasEditsBeenMade === false}
            >
              Cancel Edit
            </button>

            <button
              type="button"
              className="button -primary -light block w-full! desktop:w-auto!"
              onClick={handleSubmitRequest}
              disabled={submitLoading || hasEditsBeenMade === false}
            >
              Resubmit
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="button -secondary block w-full! desktop:w-auto!"
              onClick={() => setShowCancelModal(true)}
              disabled={submitLoading}
            >
              Cancel Request
            </button>
            <button
              type="button"
              className="button -secondary block w-full! desktop:w-auto!"
              onClick={handleSaveDraft}
              disabled={submitLoading}
            >
              Save as Draft
            </button>
            <button
              type="button"
              className="button -primary -light block w-full! desktop:w-auto!"
              onClick={handleSubmitRequest}
              disabled={submitLoading}
            >
              Submit Request
            </button>
          </>
        )}
      </div>
    </div>
  )

  // Renders the Cancel Request confirmation modal (backdrop click is disabled
  // to prevent accidental dismissal).
  // Extracted from the component's JSX purely to keep the component's own
  // Cognitive Complexity under Sonar's threshold — same markup, just relocated
  // into its own function (Sonar scores each function separately).
  const renderCancelConfirmationModal = () => (
    <Modal
      isOpen={showCancelModal}
      title="Cancel Request"
      clearBtn="No, Continue"
      submitBtn="Cancel Request"
      backBtn="Back"
      {...(!isResubmitFlow && { saveBtn: 'Save as Draft' })}
      iconSrc="/assets/images/alertSolid.svg"
      iconAlt="alert icon"
      disableBackdropClick={true}
      onSecondary={() => setShowCancelModal(false)}
      {...(!isResubmitFlow && {
        onSaveDraft: () => {
          setShowCancelModal(false)
          handleSaveDraft()
        },
      })}
      onPrimary={() => {
        setShowCancelModal(false)
        setShowHistoryTable(false)
        setComments('')
        // Clear every error surface the form can show — the parent validation
        // banner (onValidationError) as well as this component's own local
        // error state — so a stale message (e.g. a failed Save as Draft
        // payload error) doesn't linger after the request is cancelled.
        onValidationError?.(null)
        setSubmitError('')
        setCommentError('')
        setAdminReceivedDateError('')
        onCancelRequest?.()
      }}
    >
      <div className="p3 -book">
        {isResubmitFlow
          ? 'Are you sure you want to cancel this resubmission?'
          : 'You may save your changes as a draft or cancel this request without saving.'}
      </div>
    </Modal>
  )

  return (
    <div>
      {!showHistoryTable && !showActionsWithoutHistory &&
        (isAddingMoreDates && !isInDateEditMode ? (
          <p className="p3 -medium pt-2 tablet:pt-4 mobile:pt-2">
            Select a date on the Calendar to add new date in your existing
            request
          </p>
        ) : (
          <>
            {renderRequestingForSection()}

            <p className="p3 -medium pt-4">Duration</p>

            <div className="flex flex-col gap-3 pt-2">
              <label
                htmlFor="radio-duration-full"
                className={`inline-flex items-center gap-3 ${isFullDayDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  id="radio-duration-full"
                  name="radio-duration"
                  type="radio"
                  value="Full Day"
                  checked={selectedDuration === 'Full Day'}
                  onChange={() => handleDurationChange('Full Day')}
                  disabled={isFullDayDisabled}
                  aria-disabled={isFullDayDisabled}
                  className="radio-button"
                />
                {/* Use -book (regular weight) to match Figma — not -medium */}
                <span className="p3 -book">Full Day</span>
              </label>

              {!isExempt && (
                <label
                  htmlFor="radio-duration-partial"
                  className={`inline-flex items-center gap-3 ${birthdayChecked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <input
                    id="radio-duration-partial"
                    name="radio-duration"
                    type="radio"
                    value="Partial Day"
                    checked={selectedDuration === 'Partial Day'}
                    onChange={() => handleDurationChange('Partial Day')}
                    disabled={birthdayChecked}
                    className="radio-button"
                  />
                  {/* Use -book (regular weight) to match Figma — not -medium */}
                  <span className="p3 -book">Partial Day</span>
                </label>
              )}
            </div>

            {renderPartialDayTimeSection()}

            <p className="p2 -dolphin pt-4">
              The hours entered below are applied to each calendar day and
              cannot be greater than your scheduled hours.
            </p>

            <div className="pt-4">
              {/* Header Row - Stack on mobile, flex on desktop */}
              <div className="flex flex-col md:flex-row md:gap-x-1 items-center pb-3 border-b border-gray-300 w-full">
                <div className="w-full md:w-1/3 flex items-center px-0">
                  <p className="p3 -medium text-sm md:text-base text-left m-0">
                    Hours per Day
                  </p>
                </div>
                <div className="w-full md:w-1/3 flex items-center md:justify-start px-0 min-w-0">
                  <p className="p3 -medium text-sm md:text-base m-0">
                    Time Off type
                  </p>
                </div>
                <div className="w-full md:w-1/3 flex items-center md:justify-start px-0">
                  <p className="p3 -medium text-sm md:text-base m-0">
                    Resulting Balance
                  </p>
                </div>
              </div>

              {visibleLeaveRows.map(renderLeaveRow)}
            </div>

            {renderBirthdayAndHoursSection()}

            {/* Action Buttons - Responsive Layout */}
            <div className="flex flex-col-reverse desktop:flex-row  items-start gap-3 mt-6 pb-6">
              {renderActionButtons()}
            </div>
          </>
        ))}
      {/* History table with Comments - Conditionally rendered when Add to Request is clicked,
          or always in DraftView's landing state (see showActionsWithoutHistory above) */}
      {(showHistoryTable || showActionsWithoutHistory) && renderReviewAndSubmitSection()}

      {/* Cancel Confirmation Modal — backdrop click is disabled to prevent accidental dismissal */}
      {renderCancelConfirmationModal()}

      {/* Full-page loader during submission */}
      {submitLoading && <Loader />}
    </div>
  )
}

export default AddTimeToRequest